/**
 * ============================================================================
 * ISOFIN KALKULATOR - ZENTRALE RECHEN-ENGINE
 * ============================================================================
 * 
 * Modul: IsoDash.Calculations
 * Version: 3.0.0
 * 
 * SINGLE SOURCE OF TRUTH für alle Isofin®-Berechnungen.
 * Keine andere Datei darf Berechnungen durchführen.
 * 
 * ============================================================================
 * ⚠️  BREAKING CHANGES v3.0.0 - Architektur-Refactor
 * ============================================================================
 * 
 * EXTRAHIERT → 26-isodash-wiz-cart.js (IsoDash.CartSync):
 * - syncCartWithCalculation, debouncedCartSync, forceCartSync
 * - flushPendingCartSync, resetSyncGuard
 * - updateCartBadgeDOM, refreshCartBadgeFromCartJS
 * - CART_BADGE_SELECTORS, dispatchCartEvents
 * - fetchJSON, getCart, findLineItemByVariant, etc.
 * - Lifecycle-Events (pagehide, visibilitychange)
 * 
 * → Legacy-Proxy-Methoden bleiben auf IsoDash.Calculations erhalten
 *   (werden von 26-isodash-wiz-cart.js gesetzt wenn geladen).
 * 
 * GEÄNDERT: Storage
 * - Primär: IsoDash.Storage.setJSON() / getJSON() (Prefix-Namespace)
 * - Sekundär: Legacy-Keys 'isofinAnalysis' / 'isofinCalcState' werden
 *   weiterhin gespiegelt (für contact.js und andere Legacy-Consumer).
 * 
 * GEÄNDERT: Cart-Trigger
 * - calculateFinalAnalysis() dispatcht jetzt:
 *   document 'isodash-wiz:analysis-updated' { detail: { analysis, calculatedBottles } }
 * - 26-isodash-wiz-cart.js lauscht darauf und triggert debouncedSync.
 * 
 * ============================================================================
 * ⚠️  ÄNDERUNGEN v2.8.5 - Bohrmaschinen-Empfehlung + Leistungswarnung
 * ============================================================================
 * 
 * NEU: Empfiehlt passende Bohrmaschine anhand des Mauerwerkstyps.
 * Warnt bei vielen Löchern (>30) in hartem Material (härter als Ziegel).
 * 
 * DRILL_MACHINE_RECO MAP:
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ Material        │ type                  │ minWatts │ Kategorie         │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ porenbeton/ytong│ schlagbohrmaschine    │ 650      │ weich             │
 * │ bims            │ schlagbohrmaschine    │ 650      │ weich             │
 * │ ziegel          │ bohrhammer_sdsplus    │ 800      │ standard          │
 * │ hohlblock       │ bohrhammer_sdsplus    │ 800      │ standard          │
 * │ hochlochziegel  │ bohrhammer_sdsplus    │ 800      │ standard          │
 * │ kalksandstein   │ bohrhammer_sdsplus    │ 850      │ härter            │
 * │ naturstein      │ kombihammer           │ 1000     │ härter            │
 * │ beton           │ kombihammer           │ 1000     │ beton             │
 * │ stahlbeton      │ kombihammer           │ 1000     │ beton             │
 * └─────────────────────────────────────────────────────────────────────────┘
 * 
 * ============================================================================
 * HAUPTFUNKTION
 * ============================================================================
 * 
 * calculateFinalAnalysis(state) → Komplettes Analyse-Objekt
 * - Speichert via IsoDash.Storage + Legacy-Keys
 * - Dispatcht 'isodash-wiz:analysis-updated' Event (CartSync lauscht darauf)
 * 
 * ============================================================================
 * VARIABLEN-REFERENZ (analysis-Objekt)
 * ============================================================================
 * 
 * Zugriff: IsoDash.Calculations.getFinalAnalysis()
 *          oder IsoDash.Storage.getJSON('analysis')
 *          oder JSON.parse(localStorage.getItem('isofinAnalysis'))  [Legacy]
 * 
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ META / IDENTITY                                                         │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ calculatedAt          │ number   │ Timestamp (Date.now)                 │
 * │ version               │ string   │ Engine-Version                       │
 * │ schemaVersion         │ number   │ Schema-Version (2)                   │
 * │ analyseId             │ string   │ Eindeutige ID (optional)             │
 * └─────────────────────────────────────────────────────────────────────────┘
 * 
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ INPUTS (normalisiert)                                                   │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ moistureType          │ string   │ 'bottom' | 'side'                    │
 * │ barrierType           │ string   │ 'horizontal' | 'vertical'            │
 * │ barrierLabel          │ string   │ 'Horizontalsperre' | 'Flächensperre' │
 * │ masonryType           │ string   │ 'ziegel' | 'beton' | 'hohlblock'     │
 * │ wallThicknessCm       │ number   │ Wandstärke in cm                     │
 * │ wallLengthM           │ number   │ Wandlänge in m (nur Horizontal)      │
 * │ surfaceLengthM        │ number   │ Flächenlänge (nur Fläche)            │
 * │ surfaceHeightM        │ number   │ Flächenhöhe (nur Fläche)             │
 * │ cornerCount           │ number   │ Anzahl der Ecken                     │
 * └─────────────────────────────────────────────────────────────────────────┘
 * 
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ FLASCHEN & PREISE                                                       │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ calculatedBottles     │ number   │ Gesamtanzahl Flaschen (CEIL)         │
 * │ unitPrice             │ number   │ Einzelpreis nach Staffel (€)         │
 * │ totalPriceIsofin      │ number   │ Gesamtpreis Isofin® (€)             │
 * └─────────────────────────────────────────────────────────────────────────┘
 * 
 * (Vollständige Referenz aller Keys: siehe v2.8.5 Changelog)
 * 
 * ============================================================================
 * BUSINESS-REGELN
 * ============================================================================
 * 
 * WANDSTÄRKEN-FAKTOR (v2.8.2 - Grenzwert-Logik):
 * - Referenz: 36 cm = Faktor 1.0
 * - < 8 cm: Clamp auf 8 cm
 * - Bei Vielfachen von 38cm: Toleranz abziehen
 * 
 * BOHRLOCH-SPEZIFIKATIONEN:
 * - STANDARD: Bohrabstand 25 cm, 500 ml/Loch
 * - BETON: Bohrabstand 12.5 cm, 250 ml/Loch, doppelte Lochanzahl
 * 
 * PREISSTAFFEL:
 * - < 50 Flaschen:   19,90 €
 * - ≥ 50 Flaschen:   18,90 €
 * - ≥ 250 Flaschen:  17,90 €
 * 
 * ============================================================================
 * PUBLIC API
 * ============================================================================
 * 
 * IsoDash.Calculations.calculateFinalAnalysis(state)  → analysis
 * IsoDash.Calculations.getFinalAnalysis()              → analysis | null
 * IsoDash.Calculations.getWallThicknessFactor(cm)      → number
 * IsoDash.Calculations.getUnitPrice(bottleCount)       → number
 * IsoDash.Calculations.loadAnalysisFromStorage()       → analysis | null
 * IsoDash.Calculations.loadCalcStateFromStorage()      → calcState | null
 * IsoDash.Calculations.formatCurrency(value)           → string
 * IsoDash.Calculations.formatNumber(value)             → string
 * IsoDash.Calculations.generateVariationFaktor()       → number
 * IsoDash.Calculations.calculateResults(state)         → legacy object
 * IsoDash.Calculations.calculateAll(state)             → analysis (deprecated)
 * IsoDash.Calculations.getEinzelpreis(count)           → number (deprecated)
 * IsoDash.Calculations.VERSION                         → string
 * 
 * ============================================================================
 */

(function (w) {
  "use strict";

  var IsoDash = (w.IsoDash = w.IsoDash || {});
  IsoDash.__loaded = IsoDash.__loaded || {};
  // Versioned load-key to allow newer calculation bundles to override older copies.
  var FILE_KEY = "26-isodash-wiz-calculations.js::ZEITPLAN_PATCH_2026_03_05_01";
  if (IsoDash.__loaded[FILE_KEY]) {
    return;
  }
  IsoDash.__loaded[FILE_KEY] = true;

IsoDash.Calculations = (function() {
  'use strict';

  // ============================================================================
  // PRIVATE STATE
  // ============================================================================
  
  var lastAnalysis = null;

  // ============================================================================
  // INTERNAL HELPERS
  // ============================================================================

  function _isFinite(x) {
    return typeof x === 'number' && isFinite(x);
  }

  function _log() {
    try {
      if (IsoDash.Config && (IsoDash.Config.DEBUG === true || IsoDash.Config.debug === true)) {
        console.log.apply(console, arguments);
      }
    } catch(e) {}
  }

  // ============================================================================
  // KONSTANTEN
  // ============================================================================
  
  var VERSION = '3.1.0';
  
  // Legacy-Keys (für dual-write Kompatibilität)
  var LEGACY_ANALYSIS_KEY = 'isofinAnalysis';
  var LEGACY_CALCSTATE_KEY = 'isofinCalcState';
  
  // Storage-Keys (neue Architektur via IsoDash.Storage)
  var STORAGE_ANALYSIS_KEY = 'analysis';
  var STORAGE_CALCSTATE_KEY = 'calcState';
  
  // Wanddicken-Logik
  var WALL_THICKNESS_REFERENCE = 36;
  var WALL_THICKNESS_MIN = 8;
  var WALL_THICKNESS_TOLERANCE_CM = 2;
  
  var DRILL_SPACING_STANDARD = 25;
  var DRILL_SPACING_BETON = 12.5;
  
  var ML_PER_HOLE_STANDARD = 500;
  var ML_PER_HOLE_BETON = 250;
  
  var DRILL_ANGLE_DEG = 45;
  var DEFAULT_DRILL_DIAMETER_MM = 12;

  // ============================================================================
  // WORKTIME ESTIMATION
  // ============================================================================

  var WORKTIME_DEFAULT_DRILL_WATTS = 850;
  var WORKTIME_MARKING_SEC_PER_HOLE = 8;
  var WORKTIME_RANGE_DEFAULT = { low: 0.80, high: 1.25 };
  var WORKTIME_RANGE_CONCRETE = { low: 0.75, high: 1.40 };

  var WORKTIME_MATERIALS = {
    porenbeton:     { secPerCm: 1.2, handlingSec: 30 },
    ytong:          { secPerCm: 1.2, handlingSec: 30 },
    bims:           { secPerCm: 2.0, handlingSec: 32 },
    hohlblock:      { secPerCm: 2.8, handlingSec: 35 },
    hochlochziegel: { secPerCm: 2.8, handlingSec: 35 },
    ziegel:         { secPerCm: 3.5, handlingSec: 35 },
    kalksandstein:  { secPerCm: 4.8, handlingSec: 38 },
    naturstein:     { secPerCm: 6.5, handlingSec: 40 },
    beton:          { secPerCm: 8.5, handlingSec: 45 },
    stahlbeton:     { secPerCm: 13.0, handlingSec: 50 }
  };

  // Injizieren: Handling pro Loch (Verschluss auf, Einfüllverschluss rein,
  // Deckel wechseln, Flasche in Wand stecken). Flaschen laufen dann parallel leer.
  var WORKTIME_INJECTION_SEC_PER_HOLE = 45;

  // Verschließen: Mörtel anmischen (einmalig) + pro Loch zuspachteln
  var WORKTIME_SEALING_BASE_SEC = 600;        // ~10 min Mörtel anrühren + Werkzeug vorbereiten
  var WORKTIME_SEALING_SEC_PER_HOLE = 90;     // ~1,5 min pro Loch (Mörtel eindrücken, glattziehen)

  // Verschlussmörtel-Bedarf
  var MORTAR_SEAL_DEPTH_CM = 3.0;             // Sichtbare Verschlusstiefe (nicht komplette Bohrtiefe)
  var MORTAR_RESERVE_FACTOR = 1.10;           // Pauschaler Reserve-Aufschlag
  var MORTAR_KG_PER_LITER = 1.8;             // Frischmörtel ≈ 1.8 kg/l (praxisnah, nicht laborpräzise)

  // Sanierputz-Bedarf
  var SANIERPUTZ_DEFAULT_THICKNESS_CM = 2.0;
  var SANIERPUTZ_KG_PER_M2_PER_CM = 12;
  var SANIERPUTZ_BAND_HEIGHT_HORIZONTAL_M = 0.30;  // Sanierband-Höhe bei Horizontalsperre
  var SANIERPUTZ_SURFACE_RESERVE_FACTOR = 1.08;    // Flächenreserve bei Flächensperre

  // ============================================================================
  // BOHRMASCHINEN-EMPFEHLUNG + HÄRTE-RANKING
  // ============================================================================

  var DRILL_MACHINE_RECO = {
    porenbeton:   { type: 'schlagbohrmaschine', minWatts: 650,  text: 'Schlagbohrmaschine ab 650W reicht' },
    ytong:        { type: 'schlagbohrmaschine', minWatts: 650,  text: 'Schlagbohrmaschine ab 650W reicht' },
    bims:         { type: 'schlagbohrmaschine', minWatts: 650,  text: 'Schlagbohrmaschine ab 650W reicht' },
    ziegel:       { type: 'bohrhammer_sdsplus', minWatts: 800,  text: 'Bohrhammer SDS+ ab 800W empfohlen' },
    hohlblock:    { type: 'bohrhammer_sdsplus', minWatts: 800,  text: 'Bohrhammer SDS+ ab 800W empfohlen' },
    hochlochziegel:{ type: 'bohrhammer_sdsplus', minWatts: 800, text: 'Bohrhammer SDS+ ab 800W empfohlen' },
    kalksandstein:{ type: 'bohrhammer_sdsplus', minWatts: 850,  text: 'Bohrhammer SDS+ ab 850W empfohlen' },
    naturstein:   { type: 'kombihammer',        minWatts: 1000, text: 'Bohrhammer SDS+ ab 1000W — Kombihammer ideal' },
    beton:        { type: 'kombihammer',        minWatts: 1000, text: 'Bohrhammer SDS+ ab 1000W — Kombihammer nötig' },
    stahlbeton:   { type: 'kombihammer',        minWatts: 1000, text: 'Bohrhammer SDS+ ab 1000W — Kombihammer nötig' }
  };

  var MASONRY_HARDNESS_RANK = {
    porenbeton: 1, ytong: 1, bims: 1,
    ziegel: 2, hohlblock: 2, hochlochziegel: 2,
    kalksandstein: 3,
    naturstein: 4,
    beton: 5,
    stahlbeton: 6
  };

  // ============================================================================
  // FACHFIRMA PRICING
  // ============================================================================

  var FACHFIRMA_PRICING_2026 = {
    // Fixkosten (Anfahrt, Abdecken, Rüstzeit, Dokumentation)
    travelSetup: {
      horizontal: 240,
      surface: 320
    },

    // Arbeitszeit ist DER Haupttreiber
    labor: {
      // Team-Stundensatz brutto (2 Mann + Fahrzeug + Sozialabgaben + Ausfall + Admin)
      teamHourRateGross: 218
    },

    // Material/Geräte/Verbrauch (Fachfirma verkauft Material nicht zum DIY-Preis)
    material: {
      // Aufschlag auf Basis-Einzelpreis (Handling, Gewährleistung, Kalkulationspuffer, Marge)
      markupToCustomer: 1.55,

      // Verbrauch/kleines Material pro Bohrloch (Stopfen, Reiniger, Düse, Abdeckung etc.)
      consumablesPerHole: 0.55,

      // Geräte-/Rüstanteil pauschal (Bohrer/Verschleiß/Staubabsaugung etc.)
      equipment: {
        horizontal: 180,
        surface: 320
      }
    },

    // Verwaltung & Marge als realistische Anteile
    overhead: {
      // Verwaltung/Disposition/Versicherung/Gewährleistung (auf direkte Kosten)
      rate: 0.18
    },
    profit: {
      // Gewinnmarge (auf direkte Kosten + Overhead)
      rate: 0.17
    },

    // Härtefälle: Beton etc. → schlägt stark auf Zeit/Verbrauch durch
    concreteMultiplier: {
      labor: 1.22,
      consumables: 1.12,
      equipment: 1.15
    },

    // Wandstärke-Faktor: wirkt v.a. auf Zeit (Bohren/Injektion)
    caps: {
      mWallMin: 0.90,
      mWallMax: 1.40
    }
  };

  // ============================================================================
  // FORMATIERUNG
  // ============================================================================

  function formatCurrency(value) {
    if (typeof value !== 'number' || !_isFinite(value)) return '0,00';
    var parts = value.toFixed(2).split('.');
    var euro = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return euro + ',' + parts[1];
  }

  function formatNumber(value) {
    if (typeof value !== 'number' || !_isFinite(value)) return '0';
    var num = Math.round(value);
    var numStr = num.toString();
    if (numStr.length < 5) return numStr;
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  function formatDurationShort(seconds) {
    var s = Math.max(0, Math.round(Number(seconds) || 0));
    var h = Math.floor(s / 3600);
    var m = Math.round((s % 3600) / 60);
    if (h <= 0) return m + ' min';
    return h + ' h ' + m + ' min';
  }

  // ============================================================================
  // PURE HELPER FUNCTIONS
  // ============================================================================

  function _clamp(num, min, max) {
    num = Number(num);
    if (!_isFinite(num)) return min;
    return Math.min(max, Math.max(min, num));
  }

  function getMasonryHardnessRank(masonryNormalized) {
    var key = String(masonryNormalized || '').trim().toLowerCase();
    return MASONRY_HARDNESS_RANK[key] || 2;
  }

  function getDrillMachineRecommendation(masonryNormalized) {
    var key = String(masonryNormalized || '').trim().toLowerCase();
    if (DRILL_MACHINE_RECO[key]) return DRILL_MACHINE_RECO[key];
    return { type: 'bohrhammer_sdsplus', minWatts: 800, text: 'Bohrhammer SDS+ ab 800W empfohlen' };
  }

  function getDrillPowerWarning(holeCount, masonryNormalized) {
    var holes = Math.max(0, parseInt(holeCount, 10) || 0);
    if (holes <= 30) return null;
    var rank = getMasonryHardnessRank(masonryNormalized);
    var baseline = getMasonryHardnessRank('ziegel');
    if (rank <= baseline) return null;
    return (
      'Bei vielen Löchern in hartem Material: Akku-Bohrhammer spart Kraft. ' +
      'Alternativ: Pausen einlegen (Bohrer kühlen lassen).'
    );
  }

  // ============================================================================
  // WORKTIME HELPERS
  // ============================================================================

  function worktimePowerMultiplier(watts) {
    var w = _clamp(watts || WORKTIME_DEFAULT_DRILL_WATTS, 600, 1200);
    var t = (w - 600) / (1200 - 600);
    return 1.10 - t * (1.10 - 0.90);
  }

  function pickWorktimeMaterial(masonryNormalized, isConcrete) {
    var key = String(masonryNormalized || '').trim().toLowerCase();
    if (isConcrete) return { key: 'beton', cfg: WORKTIME_MATERIALS.beton };
    if (WORKTIME_MATERIALS[key]) return { key: key, cfg: WORKTIME_MATERIALS[key] };
    return { key: 'ziegel', cfg: WORKTIME_MATERIALS.ziegel };
  }

  function estimateWorktimeSeconds(opts) {
    opts = opts || {};
    var holes = Math.max(0, parseInt(opts.holes, 10) || 0);
    var depthCm = Math.max(0, parseFloat(opts.depthCm) || 0);
    var drillWatts = opts.drillWatts || WORKTIME_DEFAULT_DRILL_WATTS;
    var powerM = worktimePowerMultiplier(drillWatts);
    var handlingSec = Math.max(0, parseFloat(opts.handlingSec) || 0);
    var secPerCm = Math.max(0, parseFloat(opts.secPerCm) || 0);
    var perHoleSec = (handlingSec + (depthCm * secPerCm)) * powerM;
    var markingSecPerHole = Math.max(0, parseFloat(opts.markingSecPerHole) || WORKTIME_MARKING_SEC_PER_HOLE);
    var markingSec = holes * markingSecPerHole;
    var drillingSec = holes * perHoleSec;
    var totalSec = drillingSec + markingSec;

    return {
      perHoleSec: perHoleSec,
      drillingSec: drillingSec,
      markingSec: markingSec,
      totalSec: totalSec
    };
  }

  /**
   * Zeitplan-Step Helper: Berechnet Range + Balkenbreite für einen Arbeitsschritt
   */
  function _zeitplanStep(label, baseSec, maxSec, range) {
    var rLow = range.low || 0.80;
    var rHigh = range.high || 1.25;
    var lowMin = Math.max(0, Math.round((baseSec * rLow) / 60));
    var highMin = Math.max(0, Math.round((baseSec * rHigh) / 60));
    var pct = Math.round((baseSec / maxSec) * 100);
    return {
      label: label,
      baseSec: Math.round(baseSec),
      lowMin: lowMin,
      highMin: highMin,
      pct: pct
    };
  }

  // ============================================================================
  // KERNBERECHNUNGEN
  // ============================================================================

  /**
   * Wandstärken-Faktor (v2.8.2 - Grenzwert-Logik)
   */
  function getWallThicknessFactor(wallThickness) {
    var tRaw = parseFloat(wallThickness);
    
    if (!_isFinite(tRaw)) {
      console.warn('Isocal: Ungültige Wandstärke, verwende Faktor 1.0', wallThickness);
      return 1;
    }
    
    var t = Math.max(WALL_THICKNESS_MIN, tRaw);
    var isMultipleOf38 = (t % 38) === 0;
    
    if (isMultipleOf38) {
      var n = t / 38;
      return (t - (n * WALL_THICKNESS_TOLERANCE_CM)) / WALL_THICKNESS_REFERENCE;
    }
    
    return t / WALL_THICKNESS_REFERENCE;
  }

  /**
   * Einzelpreis nach Staffel.
   * SSOT für Mengenrabatt:
   *   < 50 Flaschen   → 19,90 €
   *   ≥ 50 Flaschen   → 18,90 €  (-5%)
   *   ≥ 250 Flaschen  → 17,90 €  (-10%)
   * Fallback-Werte sind hartkodiert, weil getUnitPrice() auch dann korrekt
   * funktionieren muss, wenn IsoDash.Config noch nicht geladen ist (z.B. wenn
   * eine gespeicherte Analyse vor dem analyse-Modul-Init wiederhergestellt wird).
   */
  var STAFFEL_FALLBACK = { base: 19.90, t50: 18.90, t250: 17.90 };
  function getUnitPrice(bottleCount) {
    var config = (IsoDash.Config && IsoDash.Config.product) || {};
    var thresholds = config.discountThresholds || {};
    var basePrice = config.basePrice || STAFFEL_FALLBACK.base;
    var t50  = thresholds[50]  || STAFFEL_FALLBACK.t50;
    var t250 = thresholds[250] || STAFFEL_FALLBACK.t250;

    if (bottleCount >= 250) return t250;
    if (bottleCount >= 50)  return t50;
    return basePrice;
  }

  /**
   * Original-Einzelpreis (ohne Staffel) — für strike-through Anzeige.
   */
  function getOriginalPrice() {
    var config = (IsoDash.Config && IsoDash.Config.product) || {};
    return config.basePrice || STAFFEL_FALLBACK.base;
  }

  /**
   * Mengenrabatt-Info: gibt zurück, ob ein Staffel-Rabatt aktiv ist und wie viel
   * der Kunde dadurch spart. Wird von Topbar + Mini-Cart genutzt.
   */
  function getDiscountInfo(bottleCount) {
    var bottles = Math.max(0, Math.round(Number(bottleCount) || 0));
    var original = getOriginalPrice();
    var unit = getUnitPrice(bottles);
    var hasDiscount = unit < original;
    return {
      bottles: bottles,
      originalUnitPrice: original,
      unitPrice: unit,
      originalTotal: bottles * original,
      finalTotal: bottles * unit,
      savedPerUnit: hasDiscount ? (original - unit) : 0,
      savedTotal: hasDiscount ? bottles * (original - unit) : 0,
      hasDiscount: hasDiscount,
      tier: bottles >= 250 ? 250 : (bottles >= 50 ? 50 : 0)
    };
  }

  /**
   * Flaschenanzahl berechnen
   */
  function calculateBottleCount(params) {
    var moistureType = params.moistureType;
    var wallLength = params.wallLength;
    var surfaceLength = params.surfaceLength;
    var surfaceHeight = params.surfaceHeight;
    var cornerCount = params.cornerCount;
    var factor = params.factor;
    
    var config = (IsoDash.Config &&
      IsoDash.Config.mauerwerksBerechnung &&
      IsoDash.Config.mauerwerksBerechnung.standard) || {};
    var horizontalFaktor = parseFloat(config.horizontalFaktor) || 4;
    var flaechenFaktor = parseFloat(config.flaechenFaktor) || 16;
    var rawCount = 0;
    
    if (moistureType === 'bottom') {
      rawCount = (
        (parseFloat(wallLength) * horizontalFaktor) +
        parseInt(cornerCount) +
        1
      ) * factor;
    } else if (moistureType === 'side') {
      var height = parseFloat(surfaceHeight);
      var length = parseFloat(surfaceLength);
      var corners = parseInt(cornerCount, 10) || 0;
      
      rawCount = (
        (height * length * flaechenFaktor) +
        (height * 4) +
        (corners * height * 4)
      ) * factor;
    }

    if (!_isFinite(rawCount) || rawCount < 0) return 0;
    return Math.max(0, Math.ceil(rawCount));
  }

  /**
   * Bohrloch-Spezifikationen
   *
   * v527 (Björn 10.09., dringend): Lochzahl und Menge je Loch waren bei jeder
   * Wandstärke außer 36 cm falsch. Bisher stand hier holeCount = bottleCount —
   * aber bottleCount ist die MIT der Wandstärke skalierte Zahl (× t/36).
   * Die Löcher hängen nur an der Länge (4 je Meter = 25 cm Raster), die
   * Wandstärke bestimmt, wie viel Material je Loch hineingeht.
   *
   * Gemessen vor dem Fix, 6 m Wand: bei 24 cm „17 Bohrlöcher · 25 cm" —
   * das deckt 4,25 m von 6 m. Bei 11,5 cm 8 Löcher = 2,00 m. Bei 49 cm
   * 35 Löcher = 8,75 m. Nur der 36-cm-Fall ging auf.
   *
   * Björns Quellformel (Vorlage aus dem alten Rechner):
   *   bohrloecher  = Länge×4 + Ecken + 1         (unskaliert)
   *   flaschen     = CEIL(bohrloecher × t/36)
   *   ml_pro_loch  = ROUND(flaschen × 500 / bohrloecher)
   *
   * Flaschenzahl und Preis ändern sich dadurch NICHT — die waren richtig.
   * Nur Lochzahl und ml je Loch. Beton weiter mit doppelter Lochzahl
   * (12,5-cm-Raster), die Menge je Loch teilt sich entsprechend.
   */
  function calculateDrillSpecs(bottleCount, isConcrete, baseHoleCount) {
    var bottles = Math.max(0, parseInt(bottleCount, 10) || 0);
    var base = parseInt(baseHoleCount, 10);
    /* Rückfall auf das alte Verhalten, falls ein Aufrufer den Basiswert nicht mitgibt. */
    if (!_isFinite(base) || base <= 0) base = bottles;

    var holes = isConcrete ? base * 2 : base;
    var mlPerHole = holes > 0
      ? Math.round((bottles * ML_PER_HOLE_STANDARD) / holes)
      : (isConcrete ? ML_PER_HOLE_BETON : ML_PER_HOLE_STANDARD);

    return {
      holeCount: holes,
      drillSpacingCm: isConcrete ? DRILL_SPACING_BETON : DRILL_SPACING_STANDARD,
      mlPerHole: mlPerHole
    };
  }

  /**
   * Bohrtiefe berechnen (Länge am Bohrer).
   * v641: gleiche Regel wie 26-isowiz-calculations.js – Handbuch (Krein) S. 29/59 + Bild 19:
   * das schräge Loch reicht etwa zwei Drittel der Wandstärke in die Wand;
   * am Bohrer ⅔ Wand ÷ cos(Winkel), aufgerundet. Vorher: bis ca. 15 cm vor dem Wandende.
   */
  function calculateDrillDepth(wallThicknessCm) {
    var thickness = parseFloat(wallThicknessCm);
    if (typeof thickness !== 'number' || !isFinite(thickness) || thickness <= 0) {
      return 0;
    }
    var angleDeg = (typeof DRILL_ANGLE_DEG === 'number') ? DRILL_ANGLE_DEG : 45;
    var angleRad = angleDeg * Math.PI / 180;
    var cosA = Math.cos(angleRad);
    if (typeof cosA !== 'number' || !isFinite(cosA) || cosA <= 0) {
      return 0;
    }
    var twoThirdsWall = thickness * 2 / 3;
    return Math.ceil(twoThirdsWall / cosA);
  }

  /**
   * Geometrische Materialabtrags-Abschätzung (Bohrlöcher)
   */
  function estimateDrillMaterialRemoval(opts) {
    opts = opts || {};
    var holes = Math.max(0, parseInt(opts.holes, 10) || 0);
    var depthCm = Math.max(0, parseFloat(opts.depthCm) || 0);
    var wallThicknessCm = Math.max(0, parseFloat(opts.wallThicknessCm) || 0);
    var diameterMm = _clamp((opts.diameterMm != null ? opts.diameterMm : DEFAULT_DRILL_DIAMETER_MM), 6, 20);
    var radiusCm = (diameterMm / 10) / 2;
    var holeAreaCm2 = Math.PI * radiusCm * radiusCm;
    var holeVolumeCm3 = holeAreaCm2 * depthCm;
    var removedVolumeCm3Total = holes * holeVolumeCm3;
    var removedVolumeLTotal = removedVolumeCm3Total / 1000;

    var referenceMode = 'surface';
    var refFaceAreaCm2 = 0;

    if (_isFinite(opts.surfaceLengthM) && _isFinite(opts.surfaceHeightM) && (opts.surfaceLengthM > 0) && (opts.surfaceHeightM > 0)) {
      var lenCm = opts.surfaceLengthM * 100;
      var hCm = opts.surfaceHeightM * 100;
      refFaceAreaCm2 = lenCm * hCm;
      referenceMode = 'surface';
    } else if (_isFinite(opts.wallLengthM) && (opts.wallLengthM > 0)) {
      var wallLenCm = opts.wallLengthM * 100;
      var refBandHeightCm = 100;
      refFaceAreaCm2 = wallLenCm * refBandHeightCm;
      referenceMode = 'wall_1m_band';
    }

    var refWallVolumeCm3 = (refFaceAreaCm2 > 0 && wallThicknessCm > 0) ? (refFaceAreaCm2 * wallThicknessCm) : 0;
    var removedVolumePercent = (refWallVolumeCm3 > 0)
      ? (removedVolumeCm3Total / refWallVolumeCm3) * 100
      : 0;
    var removedFaceAreaCm2Total = holes * holeAreaCm2;
    var removedFaceAreaPercent = (refFaceAreaCm2 > 0)
      ? (removedFaceAreaCm2Total / refFaceAreaCm2) * 100
      : 0;

    return {
      diameterMm: diameterMm,
      holeAreaCm2: holeAreaCm2,
      holeVolumeCm3: holeVolumeCm3,
      removedVolumeCm3Total: removedVolumeCm3Total,
      removedVolumeLTotal: removedVolumeLTotal,
      removedVolumePercent: removedVolumePercent,
      faceAreaCm2Total: removedFaceAreaCm2Total,
      faceAreaPercent: removedFaceAreaPercent,
      reference: {
        mode: referenceMode,
        refFaceAreaCm2: refFaceAreaCm2,
        wallThicknessCm: wallThicknessCm
      }
    };
  }

  // ═══════════════════════════════════════════════════════════════════
  // MATERIALBEDARF: Verschlussmörtel + Sanierputz
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Mauerwerksfaktor für Verschlussmörtel (Ausbrüche, Verlust, unregelmäßige Ränder)
   */
  function getMortarLossFactorByMasonry(masonryType) {
    var m = String(masonryType || '').toLowerCase();
    if (m === 'hohlblock' || m === 'hochlochziegel') return 1.70;
    if (m === 'porenbeton' || m === 'ytong' || m === 'bims') return 1.35;
    if (m === 'beton' || m === 'stahlbeton') return 1.45;
    return 1.50;
  }

  /**
   * Mauerwerksfaktor für Sanierputz (Saugverhalten, Oberflächenbeschaffenheit)
   */
  function getRenderLossFactorByMasonry(masonryType) {
    var m = String(masonryType || '').toLowerCase();
    if (m === 'hohlblock' || m === 'hochlochziegel') return 1.25;
    if (m === 'porenbeton' || m === 'ytong' || m === 'bims') return 1.10;
    if (m === 'beton' || m === 'stahlbeton') return 1.10;
    return 1.15;
  }

  /**
   * Verschlussmörtel-Bedarf: Geometrie sichtbarer Verschlussbereich (nicht ganze Bohrtiefe)
   * Volumen = π × r² × sealDepth × holeCount × masonryFactor × reserve
   */
  function calculateSealingMortarNeed(opts) {
    opts = opts || {};

    var holeCount = Math.max(0, Number(opts.holeCount) || 0);
    var diameterMm = _clamp(
      (opts.drillDiameterMm != null ? opts.drillDiameterMm : DEFAULT_DRILL_DIAMETER_MM),
      6, 20
    );
    var sealDepthCm = Math.max(0.5, Number(opts.sealDepthCm) || MORTAR_SEAL_DEPTH_CM);
    var masonryFactor = Number(opts.masonryFactor) || getMortarLossFactorByMasonry(opts.masonryType);

    var radiusCm = (diameterMm / 10) / 2;
    var volumePerHoleCm3 = Math.PI * radiusCm * radiusCm * sealDepthCm;
    var totalVolumeCm3 = holeCount * volumePerHoleCm3 * masonryFactor * MORTAR_RESERVE_FACTOR;
    var totalLiters = totalVolumeCm3 / 1000;
    var totalKgApprox = totalLiters * MORTAR_KG_PER_LITER;

    return {
      sealDepthCm: sealDepthCm,
      lossFactor: masonryFactor,
      volumeCm3: totalVolumeCm3,
      liters: totalLiters,
      kgApprox: totalKgApprox
    };
  }

  /**
   * Sanierputz-Bedarf: Fläche × Schichtdicke × Verbrauch × Mauerwerksfaktor
   * Bei Horizontalsperre: Sanierband (wallLength × 0.30m)
   * Bei Flächensperre: surfaceLength × surfaceHeight × 1.08
   */
  function calculateSanierputzNeed(opts) {
    opts = opts || {};

    var moistureType = String(opts.moistureType || 'bottom');
    var wallLengthM = Math.max(0, Number(opts.wallLengthM) || 0);
    var surfaceLengthM = Math.max(0, Number(opts.surfaceLengthM) || 0);
    var surfaceHeightM = Math.max(0, Number(opts.surfaceHeightM) || 0);
    var thicknessCm = Math.max(0.5, Number(opts.thicknessCm) || SANIERPUTZ_DEFAULT_THICKNESS_CM);
    var kgPerM2PerCm = Math.max(1, Number(opts.kgPerM2PerCm) || SANIERPUTZ_KG_PER_M2_PER_CM);
    var masonryFactor = Number(opts.masonryFactor) || getRenderLossFactorByMasonry(opts.masonryType);

    var areaM2 = 0;

    if (moistureType === 'side') {
      areaM2 = surfaceLengthM * surfaceHeightM * SANIERPUTZ_SURFACE_RESERVE_FACTOR;
    } else {
      areaM2 = wallLengthM * SANIERPUTZ_BAND_HEIGHT_HORIZONTAL_M;
    }

    var totalKg = areaM2 * thicknessCm * kgPerM2PerCm * masonryFactor;

    return {
      areaM2: areaM2,
      thicknessCm: thicknessCm,
      kgPerM2PerCm: kgPerM2PerCm,
      lossFactor: masonryFactor,
      kg: totalKg
    };
  }

  // ═══════════════════════════════════════════════════════════════════
  // MACHBARKEITS-SCORE (Feasibility) — Single Source of Truth
  // Score-Skala: 1 = einfach, 5 = aufwendig (positives Framing)
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Teilscore Mauerwerk (Spec §5.1)
   * Beton = härtester DIY-Aufwand (5), Porenbeton = leichtester (1)
   */
  function getFeasibilityMasonryScore(masonryType, isConcrete) {
    var m = String(masonryType || '').toLowerCase();
    if (isConcrete || m === 'beton' || m === 'stahlbeton') return 5;
    if (m === 'naturstein') return 4;
    if (m === 'kalksandstein') return 3;
    if (m === 'hohlblock' || m === 'hochlochziegel') return 2;
    if (m === 'ziegel') return 2;
    if (m === 'porenbeton' || m === 'ytong' || m === 'bims') return 1;
    return 2;
  }

  /**
   * Teilscore Wandstärke (Spec §5.2)
   * Dünne Wand = wenig Aufwand (1), dicke Wand = viel Aufwand (5)
   */
  function getFeasibilityThicknessScore(wallThicknessCm) {
    var cm = Number(wallThicknessCm) || 0;
    if (cm <= 24) return 1;
    if (cm <= 36) return 2;
    if (cm <= 50) return 3;
    if (cm <= 70) return 4;
    return 5;
  }

  /**
   * Teilscore Projektgröße — Horizontalsperre (Spec §5.3)
   */
  function getFeasibilityExtentScoreHorizontal(wallLengthM) {
    var m = Number(wallLengthM) || 0;
    if (m <= 5) return 1;
    if (m <= 10) return 2;
    if (m <= 20) return 3;
    if (m <= 30) return 4;
    return 5;
  }

  /**
   * Teilscore Projektgröße — Flächensperre (Spec §5.3)
   */
  function getFeasibilityExtentScoreSurface(surfaceLengthM, surfaceHeightM) {
    var area = (Number(surfaceLengthM) || 0) * (Number(surfaceHeightM) || 0);
    if (area <= 3) return 1;
    if (area <= 6) return 2;
    if (area <= 12) return 3;
    if (area <= 20) return 4;
    return 5;
  }

  /**
   * Teilscore Ecken (Spec §5.4)
   */
  function getFeasibilityCornerScore(cornerCountNum) {
    var c = Number(cornerCountNum) || 0;
    if (c <= 2) return 1;
    if (c <= 4) return 2;
    if (c <= 6) return 3;
    return 4;
  }

  /**
   * Teilscore Bohraufwand / Lochanzahl (Spec §5.5)
   */
  function getFeasibilityHoleScore(holeCount) {
    var h = Number(holeCount) || 0;
    if (h <= 30) return 1;
    if (h <= 60) return 2;
    if (h <= 100) return 3;
    if (h <= 160) return 4;
    return 5;
  }

  /**
   * Gewichteter Gesamtscore (Spec §6)
   * parts = { masonry, thickness, extent, corners, holes }
   */
  function calculateFeasibilityScore(parts) {
    var raw = (
      parts.masonry * 0.30 +
      parts.thickness * 0.22 +
      parts.extent * 0.18 +
      parts.corners * 0.10 +
      parts.holes * 0.20
    );
    /* Invertiert: hohe Zahl = einfach, niedrige = schwer */
    return Math.min(5, Math.max(1, 6 - raw));
  }

  /**
   * Score-Stufen + Badges (Spec §7)
   * Kein Rot — immer positiv formuliert
   */
  function getFeasibilityLevel(score) {
    /* Invertiert: hohe Zahl = einfach, niedrige = schwer */
    if (score >= 4.2) {
      return {
        level: 'sehr_gut_machbar',
        badge: 'Sehr gut machbar',
        shortText: 'Auch f\u00FCr Einsteiger sehr gut geeignet.',
        color: 'green',
        ampel: 'green'
      };
    }
    if (score >= 3.4) {
      return {
        level: 'gut_machbar',
        badge: 'Gut machbar',
        shortText: 'Gut DIY-umsetzbar mit normalem Werkzeug.',
        color: 'green',
        ampel: 'green'
      };
    }
    if (score >= 2.6) {
      return {
        level: 'machbar_mit_mehr_aufwand',
        badge: 'Gut machbar mit etwas mehr Aufwand',
        shortText: 'Etwas mehr Bohraufwand, aber weiterhin gut planbar.',
        color: 'yellow',
        ampel: 'yellow'
      };
    }
    if (score >= 1.8) {
      return {
        level: 'groesseres_diy_projekt',
        badge: 'Gr\u00F6\u00DFeres DIY-Projekt',
        shortText: 'Mehr Kraft- und Zeitaufwand, aber technisch klar umsetzbar.',
        color: 'yellow',
        ampel: 'yellow'
      };
    }
    return {
      level: 'anspruchsvoller_diy_fall',
      badge: 'Anspruchsvoller DIY-Fall',
      shortText: 'Hartes Material oder gr\u00F6\u00DFere Fl\u00E4che \u2013 Beratung kann sinnvoll sein.',
      color: 'orange',
      ampel: 'yellow'
    };
  }

  /**
   * Materialempfehlungen / Angst-Killer-Tipps (Spec §8)
   * Gibt { short, long, micro } pro Mauerwerktyp
   */
  function getFeasibilityMasonryTips(masonryType, isConcrete) {
    var m = String(masonryType || '').toLowerCase();
    if (isConcrete || m === 'beton' || m === 'stahlbeton') {
      return {
        short: 'Beton = deutlich h\u00E4rter zu bohren',
        long: 'F\u00FCr Beton ist ein starker Bohrhammer sinnvoll. Technisch machbar, aber k\u00F6rperlich aufwendiger.',
        micro: 'Bohrhammer empfohlen.'
      };
    }
    if (m === 'kalksandstein') {
      return {
        short: 'Kalksandstein = etwas h\u00E4rter',
        long: 'Kalksandstein ist dichter als Ziegel. Mit Bohrhammer gut machbar, aber mit etwas mehr Kraftaufwand.',
        micro: 'Bohrhammer empfohlen.'
      };
    }
    if (m === 'ziegel') {
      return {
        short: 'Ziegel = leicht bis mittel bohrbar',
        long: 'Ziegel ist im Regelfall gut DIY-tauglich. Wenn m\u00F6glich in die Lagerfuge bohren.',
        micro: 'In die Lagerfuge bohren.'
      };
    }
    if (m === 'hohlblock' || m === 'hochlochziegel') {
      return {
        short: 'Hohlblock = gut bohrbar',
        long: 'Hohlblock und Hochlochziegel sind gut bearbeitbar. Sauberes Anzeichnen ist hier wichtiger als Kraft.',
        micro: 'Sauber anzeichnen.'
      };
    }
    if (m === 'porenbeton' || m === 'ytong' || m === 'bims') {
      return {
        short: 'Por\u00F6ses Mauerwerk = sehr leicht zu bearbeiten',
        long: 'Porenbeton, Ytong oder Bims sind sehr DIY-freundlich und in der Regel leicht zu bohren.',
        micro: 'Sehr gut DIY-tauglich.'
      };
    }
    if (m === 'naturstein') {
      return {
        short: 'Naturstein = materialabh\u00E4ngig',
        long: 'Naturstein kann je nach H\u00E4rte schwanken. Technisch machbar, aber mit mehr Aufmerksamkeit beim Bohren.',
        micro: 'Etwas mehr Ruhe einplanen.'
      };
    }
    return {
      short: 'Mauerwerk = gut planbar',
      long: 'Mit den berechneten Bohrdaten bleibt die Anwendung gut strukturiert.',
      micro: 'Berechnete Bohrdaten nutzen.'
    };
  }

  /**
   * Factor-Cards / Detail-Array (Spec §9)
   * Gibt Array mit 5 Faktor-Objekten für die Projektanalyse
   */
  function buildFeasibilityFactors(params) {
    var scores = params.scores;
    var mType = String(params.masonryType || '').toLowerCase();
    var wallCm = Number(params.wallThicknessCm) || 36;
    var corners = Number(params.cornerCountNum) || 0;
    var holes = Number(params.holeCount) || 0;
    var isHorizontal = params.barrierType === 'horizontal';

    // dots: score 1-2→1 (green), score 3→2 (amber), score 4-5→3 (accent)
    function toDots(s) { return s <= 2 ? 1 : s <= 3 ? 2 : 3; }

    // Masonry display
    var masonryDisplay;
    var mLabels = {
      porenbeton: 'Porenbeton', ytong: 'Ytong', bims: 'Bims',
      ziegel: 'Ziegel', hohlblock: 'Hohlblock', hochlochziegel: 'Hochlochziegel',
      kalksandstein: 'Kalksandstein', naturstein: 'Naturstein',
      beton: 'Beton', stahlbeton: 'Stahlbeton'
    };
    var mQuality = {
      porenbeton: 'sehr leicht zu bohren', ytong: 'sehr leicht zu bohren', bims: 'sehr leicht zu bohren',
      ziegel: 'leicht bis mittel bohrbar', hohlblock: 'gut bohrbar', hochlochziegel: 'gut bohrbar',
      kalksandstein: 'etwas h\u00E4rter', naturstein: 'materialabh\u00E4ngig',
      beton: 'h\u00E4rter zu bohren', stahlbeton: 'h\u00E4rter zu bohren'
    };
    masonryDisplay = (mLabels[mType] || params.masonryType || 'Mauerwerk') + ' = ' + (mQuality[mType] || 'gut planbar');

    // Extent display
    var extentDisplay;
    if (isHorizontal) {
      var wl = Number(params.wallLengthM) || 0;
      extentDisplay = wl + ' m';
      if (wl <= 5) extentDisplay += ' = kompakt';
      else if (wl <= 10) extentDisplay += ' = \u00FCberschaubar';
      else if (wl <= 20) extentDisplay += ' = mittlerer Umfang';
      else if (wl <= 30) extentDisplay += ' = gr\u00F6\u00DFeres Projekt';
      else extentDisplay += ' = gro\u00DFes Projekt';
    } else {
      var sl = Number(params.surfaceLengthM) || 0;
      var sh = Number(params.surfaceHeightM) || 0;
      var area = Math.round(sl * sh * 10) / 10;
      extentDisplay = area + ' m\u00B2 Fl\u00E4che';
    }

    // Thickness display
    var thickDisplay = wallCm + ' cm';
    if (wallCm <= 24) thickDisplay += ' = d\u00FCnn, schnell gebohrt';
    else if (wallCm <= 36) thickDisplay += ' = Standard';
    else if (wallCm <= 50) thickDisplay += ' = mittlere Tiefe';
    else if (wallCm <= 70) thickDisplay += ' = tief';
    else thickDisplay += ' = sehr tief';

    return [
      {
        key: 'masonry',
        label: 'Mauerwerk',
        score: scores.masonry,
        display: masonryDisplay,
        dots: toDots(scores.masonry),
        tip: getFeasibilityMasonryTips(params.masonryType, params.isConcrete).micro
      },
      {
        key: 'thickness',
        label: 'Wandst\u00E4rke',
        score: scores.thickness,
        display: thickDisplay,
        dots: toDots(scores.thickness),
        tip: scores.thickness <= 2 ? 'Standard-Bohrerl\u00E4nge reicht aus.' : scores.thickness <= 3 ? 'Standard-Bohrerl\u00E4nge (60 cm) reicht.' : 'Langer Bohrer n\u00F6tig, mehr Kraft erforderlich.'
      },
      {
        key: 'extent',
        label: 'Projektgr\u00F6\u00DFe',
        score: scores.extent,
        display: extentDisplay,
        dots: toDots(scores.extent),
        tip: scores.extent <= 2 ? 'Gut in einem halben Tag planbar.' : scores.extent <= 3 ? 'Gut in einem Tag planbar.' : 'Projekt auf 2 Tage aufteilen.'
      },
      {
        key: 'corners',
        label: 'Ecken',
        score: scores.corners,
        display: corners + (corners === 1 ? ' Ecke' : ' Ecken'),
        dots: toDots(scores.corners),
        tip: scores.corners <= 1 ? 'Nur geringer Zusatzaufwand.' : scores.corners <= 2 ? 'Sauberes Einmessen n\u00F6tig.' : 'Pr\u00E4zise Markierung wichtig.'
      },
      {
        key: 'holes',
        label: 'Bohrpunkte',
        score: scores.holes,
        display: holes + ' Bohrungen',
        dots: toDots(scores.holes),
        tip: scores.holes <= 2 ? '\u00DCberschaubarer Aufwand.' : scores.holes <= 3 ? 'Pausen einplanen.' : 'Projekt auf 2 Tage aufteilen.'
      }
    ];
  }

  /**
   * Fachfirma-Vergleichskosten (Detailed)
   */
  function calculateFachfirmaPriceDetailed(params) {
    var moistureType = params.moistureType;
    var wallThicknessFactor = params.wallThicknessFactor;
    var isConcrete = params.isConcrete;
    var holeCount = params.holeCount;
    var bottleCount = params.bottleCount;
    var areaM2 = params.areaM2;
    var wallLengthM = params.wallLengthM;
    var variationFaktor = params.variationFaktor;
    var workTimeTotalMin = params.workTimeTotalMin;

    var PR = FACHFIRMA_PRICING_2026;

    // Faktoren
    var m_wall = _clamp(
      0.90 + 0.18 * (Number(wallThicknessFactor) || 1),
      PR.caps.mWallMin,
      PR.caps.mWallMax
    );

    var m_variation = _isFinite(parseFloat(variationFaktor)) ? parseFloat(variationFaktor) : 1;

    var m_conc_labor = isConcrete ? PR.concreteMultiplier.labor : 1.00;
    var m_conc_cons  = isConcrete ? PR.concreteMultiplier.consumables : 1.00;
    var m_conc_equip = isConcrete ? PR.concreteMultiplier.equipment : 1.00;

    // Arbeitszeit (Min → Stunden)
    var teamHourRateGross = PR.labor.teamHourRateGross;
    var teamHours = Math.max(0, (Number(workTimeTotalMin) || 0) / 60);

    // Direktkosten: Labor
    var labor = teamHours * teamHourRateGross * m_wall * m_conc_labor * m_variation;

    // Direktkosten: Material (bottleCount * Basispreis * Fachfirma-Aufschlag)
    var cfg = (IsoDash.Config && IsoDash.Config.product) || {};
    var baseUnitPrice = cfg.basePrice || 19.90; // bewusst ohne DIY-Rabatte
    var bcSource = Number(bottleCount);
    if (!_isFinite(bcSource) || bcSource <= 0) {
      // Fallback: derive bottle count from holes if caller forgot to pass bottleCount.
      var fallbackHoles = Math.max(0, Number(holeCount) || 0);
      bcSource = isConcrete ? (fallbackHoles / 2) : fallbackHoles;
    }
    var bc = Math.max(0, bcSource);
    var materials = bc * baseUnitPrice * PR.material.markupToCustomer;

    // Direktkosten: Verbrauch & Equipment
    var hc = Math.max(0, Number(holeCount) || 0);
    var consumables = hc * PR.material.consumablesPerHole * m_conc_cons * m_variation;

    var travel = (moistureType === 'bottom') ? PR.travelSetup.horizontal : PR.travelSetup.surface;
    var equipBase = (moistureType === 'bottom') ? PR.material.equipment.horizontal : PR.material.equipment.surface;
    var equipment = equipBase * m_conc_equip * m_variation;

    // Overhead & Gewinn
    var direct = labor + materials + consumables + equipment;
    var overhead = direct * PR.overhead.rate;
    var profit = (direct + overhead) * PR.profit.rate;

    var total = travel + direct + overhead + profit;

    // Breakdown passend zu deiner UI (4 Zeilen)
    var breakdownTravel = travel;
    var breakdownLabor = labor;
    var breakdownMaterialsAndDevices = materials + consumables + equipment;
    var breakdownOverheadAndMargin = overhead + profit;

    return {
      total: Math.round(total),
      breakdown: {
        travel: Math.round(breakdownTravel),
        labor: Math.round(breakdownLabor),
        materials: Math.round(breakdownMaterialsAndDevices),
        overhead: Math.round(breakdownOverheadAndMargin)
      },
      multipliers: {
        m_wall: +m_wall.toFixed(4),
        m_variation: +m_variation.toFixed(4),
        m_conc_labor: +m_conc_labor.toFixed(4)
      },
      meta: {
        teamHourRateGross: teamHourRateGross,
        teamHours: +teamHours.toFixed(2),
        baseUnitPrice: +baseUnitPrice.toFixed(2),
        markupToCustomer: PR.material.markupToCustomer,
        bottleCount: bc,
        holeCount: hc
      }
    };
  }

  /**
   * Legacy-Wrapper: gibt nur Gesamtpreis zurück
   */
  function calculateFachfirmaPrice(params) {
    return calculateFachfirmaPriceDetailed(params).total;
  }

  /**
   * Mauerwerkstyp normalisieren und Flags ermitteln
   */
  function parseMasonryType(masonryType) {
    var normalized = String(masonryType || 'ziegel').trim().toLowerCase();
    var isStampfbeton = normalized === 'stampfbeton' || normalized.indexOf('stampf') >= 0;
    var isConcrete = !isStampfbeton && (normalized === 'beton' || normalized === 'stahlbeton' || normalized.indexOf('beton') >= 0);
    var isHollowBlock = normalized.indexOf('hohl') >= 0 || normalized.indexOf('loch') >= 0;

    return {
      normalized: normalized,
      isConcrete: isConcrete,
      isStampfbeton: isStampfbeton,
      isHollowBlock: isHollowBlock
    };
  }

  /**
   * Barrier-Type und Label ermitteln
   */
  function getBarrierInfo(moistureType) {
    if (moistureType === 'bottom') {
      return {
        barrierType: 'horizontal',
        barrierLabel: 'Horizontalsperre'
      };
    }
    return {
      barrierType: 'vertical',
      barrierLabel: 'Flächensperre'
    };
  }

  // ============================================================================
  // STORAGE (IsoDash.Storage + Legacy Dual-Write)
  // ============================================================================

  /**
   * Prüft ob IsoDash.Storage verfügbar ist
   */
  function _hasStorage() {
    return !!(IsoDash.Storage && typeof IsoDash.Storage.setJSON === 'function');
  }

  /**
   * Analyse speichern
   * - Primär: IsoDash.Storage (Prefix-Namespace)
   * - Sekundär: Legacy localStorage Key (für contact.js etc.)
   */
  function saveAnalysisToStorage(analysis) {
    /* P6: Timestamp für Welcome-Back-Banner */
    try { localStorage.setItem('isodash_last_calc_at', String(Date.now())); } catch(_) {}

    // Primär: IsoDash.Storage
    if (_hasStorage()) {
      try {
        IsoDash.Storage.setJSON(STORAGE_ANALYSIS_KEY, analysis);
      } catch (error) {
        console.error('Isocal: Storage.setJSON("analysis") failed', error);
      }
    }
    
    // Sekundär: Legacy-Key (dual-write für contact.js Fallback)
    try {
      localStorage.setItem(LEGACY_ANALYSIS_KEY, JSON.stringify(analysis));
    } catch (error) {
      console.error('Isocal: Legacy localStorage write failed', error);
    }
  }

  /**
   * Analyse laden
   * - Primär: IsoDash.Storage
   * - Fallback: Legacy localStorage Key
   */
  function loadAnalysisFromStorage() {
    // Primär: IsoDash.Storage
    if (_hasStorage()) {
      try {
        var data = IsoDash.Storage.getJSON(STORAGE_ANALYSIS_KEY, null);
        if (data) return data;
      } catch (error) {
        console.warn('Isocal: Storage.getJSON("analysis") failed', error);
      }
    }
    
    // Fallback: Legacy-Key
    try {
      var raw = localStorage.getItem(LEGACY_ANALYSIS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error('Isocal: Legacy localStorage read failed', error);
      return null;
    }
  }

  /**
   * CalcState speichern (kompakter UI-State für Rehydrierung)
   */
  function saveCalcStateToStorage(analysis) {
    var calcState = {
      // Meta
      savedAt: Date.now(),
      schemaVersion: 2,
      
      // Inputs
      moistureType: analysis.moistureType,
      masonryType: analysis.masonryType,
      wallThicknessCm: analysis.wallThicknessCm,
      wallLengthM: analysis.wallLengthM,
      surfaceLengthM: analysis.surfaceLengthM,
      surfaceHeightM: analysis.surfaceHeightM,
      cornerCount: analysis.cornerCount,
      
      // Outputs
      calculatedBottles: analysis.calculatedBottles,
      
      // Flaschenverteilung
      bottlesPerCorner: analysis.bottlesPerCorner,
      cornerBottleCountTotal: analysis.cornerBottleCountTotal,
      finishingBottleCount: analysis.finishingBottleCount,
      straightSectionBottles: analysis.straightSectionBottles,
      
      unitPrice: analysis.unitPrice,
      totalPriceIsofin: analysis.totalPriceIsofin,
      fachfirmaPrice: analysis.fachfirmaPrice,
      savingsAmount: analysis.savingsAmount,
      savingsPercent: analysis.savingsPercent,
      
      // Drill-Info
      holeCount: analysis.holeCount,
      drillSpacingCm: analysis.drillSpacingCm,
      drillDepthCm: analysis.drillDepthCm,
      drillDiameterMm: analysis.drillDiameterMm,
      drillRemovedVolumePercent: analysis.drillRemovedVolumePercent,
      drillRemovedFaceAreaPercent: analysis.drillRemovedFaceAreaPercent,
      
      // Bohrmaschinen-Empfehlung
      drillMachineType: analysis.drillMachineType,
      drillMachineMinWatts: analysis.drillMachineMinWatts,
      drillMachineRecommendation: analysis.drillMachineRecommendation,
      drillMachinePowerWarning: analysis.drillMachinePowerWarning,
      
      // Worktime
      workTimeTotalMin: analysis.workTimeTotalMin,
      workTimeRangeMin: analysis.workTimeRangeMin,
      workTimeTextShort: analysis.workTimeTextShort,
      workTimeRangeText: analysis.workTimeRangeText,
      
      // Zeitplan (4-Phasen-Aufschlüsselung)
      zeitplan: analysis.zeitplan,
      
      // Flags
      isConcrete: analysis.isConcrete,
      barrierType: analysis.barrierType
    };
    
    // Primär: IsoDash.Storage
    if (_hasStorage()) {
      try {
        IsoDash.Storage.setJSON(STORAGE_CALCSTATE_KEY, calcState);
      } catch (error) {
        console.error('Isocal: Storage.setJSON("calcState") failed', error);
      }
    }
    
    // Sekundär: Legacy-Key
    try {
      localStorage.setItem(LEGACY_CALCSTATE_KEY, JSON.stringify(calcState));
      _log('Isocal: CalcState saved', calcState);
    } catch (error) {
      console.error('Isocal: Legacy CalcState write failed', error);
    }
  }

  /**
   * CalcState laden
   */
  function loadCalcStateFromStorage() {
    // Primär: IsoDash.Storage
    if (_hasStorage()) {
      try {
        var data = IsoDash.Storage.getJSON(STORAGE_CALCSTATE_KEY, null);
        if (data) return data;
      } catch (error) {
        console.warn('Isocal: Storage.getJSON("calcState") failed', error);
      }
    }
    
    // Fallback: Legacy-Key
    try {
      var raw = localStorage.getItem(LEGACY_CALCSTATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error('Isocal: Legacy CalcState read failed', error);
      return null;
    }
  }

  // ============================================================================
  // EVENT DISPATCH (fachliches Event, kein DOM/Cart)
  // ============================================================================

  /**
   * Dispatcht 'isodash-wiz:analysis-updated' Event
   * CartSync lauscht darauf und triggert debouncedSync.
   */
  function dispatchAnalysisUpdated(analysis) {
    try {
      document.dispatchEvent(new CustomEvent('isodash-wiz:analysis-updated', {
        bubbles: true,
        detail: {
          analysis: analysis,
          calculatedBottles: analysis.calculatedBottles,
          timestamp: Date.now()
        }
      }));
    } catch(e) {
      console.warn('Isocal: dispatchAnalysisUpdated failed', e);
    }
  }

  // ============================================================================
  // ZENTRALE HAUPTFUNKTION
  // ============================================================================

  /**
   * ZENTRALE BERECHNUNGSFUNKTION - Single Source of Truth
   * 
   * Berechnet ALLE Werte, speichert in Storage, dispatcht Event.
   * Cart-Sync wird NICHT mehr direkt aufgerufen — stattdessen
   * wird 'isodash-wiz:analysis-updated' dispatcht (CartSync lauscht darauf).
   * 
   * @param {Object} state - Eingabewerte
   * @returns {Object} Komplettes Analyse-Objekt
   */
  function calculateFinalAnalysis(state) {
    var moistureType = state.moistureType;
    var masonryType = state.masonryType;
    var wallLength = state.wallLength;
    var surfaceLength = state.surfaceLength;
    var surfaceHeight = state.surfaceHeight;
    var cornerCount = state.cornerCount;
    var wallThickness = state.wallThickness;
    var variationFaktor = state.variationFaktor;

    // 1. Inputs normalisieren
    var wallThicknessCm = parseFloat(wallThickness) || WALL_THICKNESS_REFERENCE;
    var wallLengthM = moistureType === 'bottom' ? parseFloat(wallLength) || 0 : null;
    var surfaceLengthM = moistureType === 'side' ? parseFloat(surfaceLength) || 0 : null;
    var surfaceHeightM = moistureType === 'side' ? parseFloat(surfaceHeight) || 0 : null;
    var cornerCountNum = parseInt(cornerCount) || 0;
    
    // 2. Mauerwerk-Flags
    var masonry = parseMasonryType(masonryType);
    
    // 3. Barrier-Info
    var barrier = getBarrierInfo(moistureType);
    
    // 4. Wandstärken-Faktor
    var wallThicknessFactor = getWallThicknessFactor(wallThicknessCm);
    
    // 5. Flaschenanzahl
    var calculatedBottles = calculateBottleCount({
      moistureType: moistureType,
      wallLength: wallLength,
      surfaceLength: surfaceLength,
      surfaceHeight: surfaceHeight,
      cornerCount: cornerCount,
      factor: wallThicknessFactor
    });
    
    // 5b. Flaschenverteilung (v2.8)
    var bottlesPerCorner = 0;
    var cornerBottleCountTotal = 0;
    var finishingBottleCount = 0;
    var straightSectionBottles = 0;
    
    if (moistureType === 'bottom') {
      bottlesPerCorner = 1;
      cornerBottleCountTotal = cornerCountNum * bottlesPerCorner;
      finishingBottleCount = 1;
      straightSectionBottles = Math.max(0, calculatedBottles - cornerBottleCountTotal - finishingBottleCount);
    } else if (moistureType === 'side') {
      var heightVal = surfaceHeightM || 0;
      var rawPerCorner = heightVal * 4 * wallThicknessFactor;
      bottlesPerCorner = Math.max(0, Math.floor(rawPerCorner));
      cornerBottleCountTotal = cornerCountNum * bottlesPerCorner;
      finishingBottleCount = 0;
      straightSectionBottles = Math.max(0, calculatedBottles - cornerBottleCountTotal);
    }
    
    // Sicherheit
    if (cornerBottleCountTotal > calculatedBottles) {
      cornerBottleCountTotal = calculatedBottles;
      bottlesPerCorner = cornerCountNum > 0 ? Math.floor(cornerBottleCountTotal / cornerCountNum) : 0;
      cornerBottleCountTotal = bottlesPerCorner * cornerCountNum;
      straightSectionBottles = calculatedBottles - cornerBottleCountTotal;
      finishingBottleCount = 0;
    }
    
    // 6. Bohrloch-Spezifikationen
    // v527: Lochzahl aus der UNSKALIERTEN Basis (Faktor 1), nicht aus der
    // Flaschenzahl — siehe calculateDrillSpecs.
    var baseHoleCount = calculateBottleCount({
      moistureType: moistureType,
      wallLength: wallLength,
      surfaceLength: surfaceLength,
      surfaceHeight: surfaceHeight,
      cornerCount: cornerCount,
      factor: 1
    });
    var drillSpecs = calculateDrillSpecs(calculatedBottles, masonry.isConcrete, baseHoleCount);
    var drillDepthCm = calculateDrillDepth(wallThicknessCm);

    var drillDiameterMm = (state && (state.drillDiameterMm != null || state.holeDiameterMm != null))
      ? parseFloat(state.drillDiameterMm != null ? state.drillDiameterMm : state.holeDiameterMm)
      : DEFAULT_DRILL_DIAMETER_MM;

    var drillRemoval = estimateDrillMaterialRemoval({
      holes: drillSpecs.holeCount,
      depthCm: drillDepthCm,
      diameterMm: drillDiameterMm,
      wallThicknessCm: wallThicknessCm,
      surfaceLengthM: surfaceLengthM,
      surfaceHeightM: surfaceHeightM,
      wallLengthM: wallLengthM
    });

    // 6a-ii. Verschlussmörtel-Bedarf
    var sealingMortar = calculateSealingMortarNeed({
      holeCount: drillSpecs.holeCount,
      drillDiameterMm: drillDiameterMm,
      sealDepthCm: state.sealingMortarSealDepthCm,
      masonryType: masonry.normalized
    });

    // 6a-iii. Sanierputz-Bedarf (geschätzt)
    var sanierputz = calculateSanierputzNeed({
      moistureType: moistureType,
      wallLengthM: wallLengthM,
      surfaceLengthM: surfaceLengthM,
      surfaceHeightM: surfaceHeightM,
      thicknessCm: state.sanierputzThicknessCm,
      kgPerM2PerCm: state.sanierputzKgPerM2PerCm,
      masonryType: masonry.normalized
    });

    // 6b. Worktime-Schätzung
    var drillWatts = state.drillWatts || WORKTIME_DEFAULT_DRILL_WATTS;
    var matPick = pickWorktimeMaterial(masonry.normalized, masonry.isConcrete);
    var worktimeRange = masonry.isConcrete ? WORKTIME_RANGE_CONCRETE : WORKTIME_RANGE_DEFAULT;

    var wt = estimateWorktimeSeconds({
      holes: drillSpecs.holeCount,
      depthCm: drillDepthCm,
      drillWatts: drillWatts,
      handlingSec: matPick.cfg.handlingSec,
      secPerCm: matPick.cfg.secPerCm,
      markingSecPerHole: WORKTIME_MARKING_SEC_PER_HOLE
    });

    var workTimeTotalMin = Math.max(0, Math.round(wt.totalSec / 60));
    var rangeLowMin = Math.max(0, Math.round((wt.totalSec * worktimeRange.low) / 60));
    var rangeHighMin = Math.max(0, Math.round((wt.totalSec * worktimeRange.high) / 60));

    // 6c. Injizieren & Verschließen (für Zeitplan)
    // Injizieren = reines Handling pro Loch: Verschluss auf, Einfüllverschluss rein,
    // Deckel wechseln, Flasche reinstecken. Flaschen laufen danach parallel leer.
    var injectionSec = drillSpecs.holeCount * WORKTIME_INJECTION_SEC_PER_HOLE;

    // Flaschen leerlaufen lassen = PASSIVE Wartezeit bis Flaschen leergelaufen sind.
    // Abhängig von Porosität, Durchfeuchtung, Wanddicke und Material.
    // Handbuch: Stampfbeton/Hohlblock/Bruchstein = "wenige Minuten", Max = 10 Tage (äußerst selten).
    var drainHoursLow, drainHoursHigh;
    if (masonry.isConcrete) {
      drainHoursLow = 24; drainHoursHigh = 72;     // Stahlbeton (gegossen): 1–3 Tage
    } else if (masonry.isStampfbeton) {
      drainHoursLow = 0.1; drainHoursHigh = 2;     // Stampfbeton: Minuten bis 2 Std (sehr porös lt. Handbuch)
    } else if (masonry.isHollowBlock) {
      drainHoursLow = 0.1; drainHoursHigh = 4;     // Hohlblock/Gitterziegel: Minuten bis 4 Std (lt. Handbuch S.103)
    } else if (['naturstein','bruchstein'].indexOf(masonry.normalized) !== -1) {
      drainHoursLow = 0.1; drainHoursHigh = 4;     // Bruchstein/Naturstein: Minuten bis 4 Std (weiche Mörtelfugen)
    } else if (wallThicknessCm >= 50) {
      drainHoursLow = 12; drainHoursHigh = 48;     // Dicke Wand: 12h–2 Tage
    } else if (['porenbeton','ytong','gasbeton'].indexOf(masonry.normalized) !== -1) {
      drainHoursLow = 0.5; drainHoursHigh = 4;     // Porenbeton/Ytong: 30 Min–4 Std
    } else {
      drainHoursLow = 4; drainHoursHigh = 24;      // Standard Ziegel/KS: 4h–1 Tag
    }

    // Verschließen = Mörtel anrühren (einmalig) + pro Loch zuspachteln
    var sealingSec = WORKTIME_SEALING_BASE_SEC + (drillSpecs.holeCount * WORKTIME_SEALING_SEC_PER_HOLE);

    // Markieren-Minimum: Schnurschlag + Messen dauert realistisch mind. 15 min
    var markingSecAdj = Math.max(wt.markingSec, 900);

    // 6d. Zeitplan-Objekt (5 Phasen: 4 aktiv + 1 passiv)
    var zpMaxSec = Math.max(markingSecAdj, wt.drillingSec, injectionSec, sealingSec, 1);
    var zeitplanSteps = [
      _zeitplanStep('Markieren', markingSecAdj, zpMaxSec, worktimeRange),
      _zeitplanStep('Bohren', wt.drillingSec, zpMaxSec, worktimeRange),
      _zeitplanStep('Injizieren', injectionSec, zpMaxSec, worktimeRange),
      {
        label: 'Flaschen leerlaufen lassen',
        type: 'passive',
        baseSec: 0,
        lowMin: 0,
        highMin: 0,
        pct: 100,
        drainHoursLow: drainHoursLow,
        drainHoursHigh: drainHoursHigh,
        note: 'Alle Flaschen einsetzen und leerlaufen lassen. Erst danach entfernen und Bohrlo\u0308cher mit Mo\u0308rtel verschlie\u00dfen.'
      },
      _zeitplanStep('Verschlie\u00DFen', sealingSec, zpMaxSec, worktimeRange)
    ];
    // Gesamt-Arbeitszeit: nur aktive Schritte (ohne passive Wartezeit)
    var zpTotalLowMin = 0, zpTotalHighMin = 0;
    for (var zi = 0; zi < zeitplanSteps.length; zi++) {
      if (zeitplanSteps[zi].type !== 'passive') {
        zpTotalLowMin += zeitplanSteps[zi].lowMin;
        zpTotalHighMin += zeitplanSteps[zi].highMin;
      }
    }
    
    // 7. Preise
    var unitPrice = getUnitPrice(calculatedBottles);
    var totalPriceIsofin = calculatedBottles * unitPrice;
    
    var fachfirmaResult = calculateFachfirmaPriceDetailed({
      moistureType: moistureType,
      wallThicknessFactor: wallThicknessFactor,
      isConcrete: masonry.isConcrete,
      holeCount: drillSpecs.holeCount,
      bottleCount: calculatedBottles,
      areaM2: (moistureType === 'side' && surfaceLengthM && surfaceHeightM)
        ? surfaceLengthM * surfaceHeightM : 0,
      wallLengthM: wallLengthM || 0,
      variationFaktor: variationFaktor || 1,
      workTimeTotalMin: workTimeTotalMin
    });
    var fachfirmaPrice = fachfirmaResult.total;
    
    // 8. Einsparungen
    var savingsAmount = fachfirmaPrice - totalPriceIsofin;
    var savingsPercent = fachfirmaPrice > 0
      ? Math.round((savingsAmount / fachfirmaPrice) * 100)
      : 0;
    
    // 9. Bohrmaschinen-Empfehlung
    var drillMachineReco = getDrillMachineRecommendation(masonry.normalized);
    var drillPowerWarning = getDrillPowerWarning(drillSpecs.holeCount, masonry.normalized);

    // 9b. Machbarkeits-Score (Feasibility SSOT)
    var fMasonry = getFeasibilityMasonryScore(masonry.normalized, masonry.isConcrete);
    var fThickness = getFeasibilityThicknessScore(wallThicknessCm);
    var fExtent = barrier.barrierType === 'horizontal'
      ? getFeasibilityExtentScoreHorizontal(wallLengthM)
      : getFeasibilityExtentScoreSurface(surfaceLengthM, surfaceHeightM);
    var fCorners = getFeasibilityCornerScore(cornerCountNum);
    var fHoles = getFeasibilityHoleScore(drillSpecs.holeCount);

    var feasibilityRaw = calculateFeasibilityScore({
      masonry: fMasonry, thickness: fThickness,
      extent: fExtent, corners: fCorners, holes: fHoles
    });
    var feasibilityScore = Math.round(feasibilityRaw * 10) / 10;
    var feasibilityRounded = Math.max(1, Math.min(5, Math.round(feasibilityRaw)));
    var feasibilityLevel = getFeasibilityLevel(feasibilityRaw);
    var feasibilityTips = getFeasibilityMasonryTips(masonry.normalized, masonry.isConcrete);
    var feasibilityFactors = buildFeasibilityFactors({
      masonryType: masonry.normalized,
      wallThicknessCm: wallThicknessCm,
      wallLengthM: wallLengthM,
      surfaceLengthM: surfaceLengthM,
      surfaceHeightM: surfaceHeightM,
      cornerCountNum: cornerCountNum,
      holeCount: drillSpecs.holeCount,
      barrierType: barrier.barrierType,
      isConcrete: masonry.isConcrete,
      scores: {
        masonry: fMasonry, thickness: fThickness,
        extent: fExtent, corners: fCorners, holes: fHoles
      }
    });

    // 10. Analyse-Objekt
    var analysis = {
      // Identity / Meta
      calculatedAt: Date.now(),
      version: VERSION,
      schemaVersion: 2,
      analyseId: state.analysisId || state.analyseId || null,
      
      // Inputs (normalisiert)
      moistureType: moistureType || 'bottom',
      barrierType: barrier.barrierType,
      barrierLabel: barrier.barrierLabel,
      masonryType: masonry.normalized,
      wallThicknessCm: wallThicknessCm,
      wallLengthM: wallLengthM,
      surfaceLengthM: surfaceLengthM,
      surfaceHeightM: surfaceHeightM,
      cornerCount: cornerCountNum,
      
      // Factor / Rules
      wallThicknessFactor: wallThicknessFactor,
      
      // Isofin Amounts
      calculatedBottles: calculatedBottles,
      
      // Flaschenverteilung (v2.8)
      bottlesPerCorner: bottlesPerCorner,
      cornerBottleCountTotal: cornerBottleCountTotal,
      finishingBottleCount: finishingBottleCount,
      straightSectionBottles: straightSectionBottles,
      
      unitPrice: unitPrice,
      totalPriceIsofin: totalPriceIsofin,
      
      // Drill Specs (flat)
      holeCount: drillSpecs.holeCount,
      drillSpacingCm: drillSpecs.drillSpacingCm,
      drillAngleDeg: DRILL_ANGLE_DEG,
      drillDepthCm: drillDepthCm,
      mlPerHole: drillSpecs.mlPerHole,

      // Bohrmaschinen-Empfehlung (flat)
      drillMachineType: drillMachineReco.type,
      drillMachineMinWatts: drillMachineReco.minWatts,
      drillMachineRecommendation: drillMachineReco.text,
      drillMachinePowerWarning: drillPowerWarning,

      // Materialabtrags-Info (flat)
      drillDiameterMm: drillRemoval.diameterMm,
      drillHoleAreaCm2: drillRemoval.holeAreaCm2,
      drillHoleVolumeCm3: drillRemoval.holeVolumeCm3,
      drillRemovedVolumeCm3Total: drillRemoval.removedVolumeCm3Total,
      drillRemovedVolumeLTotal: drillRemoval.removedVolumeLTotal,
      drillRemovedVolumePercent: drillRemoval.removedVolumePercent,
      drillRemovedFaceAreaCm2Total: drillRemoval.faceAreaCm2Total,
      drillRemovedFaceAreaPercent: drillRemoval.faceAreaPercent,
      drillRemovalReference: drillRemoval.reference,
      
      // Drill Specs (nested)
      drill: {
        holeCount: drillSpecs.holeCount,
        spacingCm: drillSpecs.drillSpacingCm,
        angleDeg: DRILL_ANGLE_DEG,
        depthCm: drillDepthCm,
        mlPerHole: drillSpecs.mlPerHole,
        diameterMm: drillRemoval.diameterMm,
        removedVolumePercent: drillRemoval.removedVolumePercent,
        removedFaceAreaPercent: drillRemoval.faceAreaPercent
      },

      // Bohrmaschinen-Empfehlung (nested)
      drillMachine: {
        type: drillMachineReco.type,
        minWatts: drillMachineReco.minWatts,
        text: drillMachineReco.text,
        powerWarning: drillPowerWarning
      },

      // Verschlussmörtel-Bedarf (flat)
      sealingMortarSealDepthCm: sealingMortar.sealDepthCm,
      sealingMortarLossFactor: sealingMortar.lossFactor,
      sealingMortarVolumeCm3: sealingMortar.volumeCm3,
      sealingMortarLiters: sealingMortar.liters,
      sealingMortarKgApprox: sealingMortar.kgApprox,

      // Sanierputz-Bedarf (flat, geschätzt)
      sanierputzAreaM2: sanierputz.areaM2,
      sanierputzThicknessCm: sanierputz.thicknessCm,
      sanierputzKgPerM2PerCm: sanierputz.kgPerM2PerCm,
      sanierputzLossFactor: sanierputz.lossFactor,
      sanierputzKg: sanierputz.kg,

      // Materialbedarf (nested)
      materials: {
        sealingMortar: {
          sealDepthCm: sealingMortar.sealDepthCm,
          lossFactor: sealingMortar.lossFactor,
          volumeCm3: sealingMortar.volumeCm3,
          liters: sealingMortar.liters,
          kgApprox: sealingMortar.kgApprox
        },
        sanierputz: {
          areaM2: sanierputz.areaM2,
          thicknessCm: sanierputz.thicknessCm,
          kgPerM2PerCm: sanierputz.kgPerM2PerCm,
          lossFactor: sanierputz.lossFactor,
          kg: sanierputz.kg
        }
      },

      // Worktime Estimate
      workTimeDrillingSec: wt.drillingSec,
      workTimeMarkingSec: wt.markingSec,
      workTimeTotalSec: wt.totalSec,
      workTimeTotalMin: workTimeTotalMin,
      workTimeRangeMin: {
        low: rangeLowMin,
        mid: workTimeTotalMin,
        high: rangeHighMin
      },
      workTimePerHoleSec: wt.perHoleSec,
      workTimeTextShort: 'ca. ' + formatDurationShort(wt.totalSec),
      workTimeRangeText: 'ca. ' + rangeLowMin + '–' + rangeHighMin + ' min',
      workTimeAssumptions: {
        drillWatts: drillWatts,
        materialKey: matPick.key,
        secPerCm: matPick.cfg.secPerCm,
        handlingSec: matPick.cfg.handlingSec,
        markingSecPerHole: WORKTIME_MARKING_SEC_PER_HOLE,
        range: worktimeRange
      },

      // Injizieren & Verschließen (Zeitplan-Phasen)
      workTimeInjectionSec: injectionSec,
      workTimeSealingSec: sealingSec,
      workTimeInjectionSecPerHole: WORKTIME_INJECTION_SEC_PER_HOLE,
      workTimeSealingSecPerHole: WORKTIME_SEALING_SEC_PER_HOLE,
      workTimeSealingBaseSec: WORKTIME_SEALING_BASE_SEC,

      // Zeitplan: Komplette 4-Phasen-Aufschlüsselung für UI
      zeitplan: {
        steps: zeitplanSteps,
        totalLowMin: zpTotalLowMin,
        totalHighMin: zpTotalHighMin,
        range: worktimeRange
      },
      
      // Firm Comparison
      fachfirmaPrice: fachfirmaPrice,
      fachfirmaBreakdown: fachfirmaResult.breakdown,
      fachfirmaMultipliers: fachfirmaResult.multipliers,
      fachfirmaMeta: fachfirmaResult.meta,
      savingsAmount: savingsAmount,
      savingsPercent: savingsPercent,

      // Feasibility / Machbarkeits-Score (SSOT)
      feasibilityScore: feasibilityScore,
      feasibilityScoreRounded: feasibilityRounded,
      feasibilityAmpel: feasibilityLevel.ampel,
      feasibilityLevelKey: feasibilityLevel.level,
      feasibilityBadge: feasibilityLevel.badge,
      feasibilityTextShort: feasibilityLevel.shortText,
      feasibilityColor: feasibilityLevel.color,
      feasibilityTips: feasibilityTips,
      feasibilityFactors: feasibilityFactors,

      // Material Flags
      isConcrete: masonry.isConcrete,
      isHollowBlock: masonry.isHollowBlock,
      
      // Legacy Aliases
      spacing: drillSpecs.drillSpacingCm,
      angle: DRILL_ANGLE_DEG,
      drillDepth: drillDepthCm,
      wallThickness: wallThicknessCm,
      fachfirmaKosten: fachfirmaPrice
    };
    
    // 11. Speichern (IsoDash.Storage + Legacy dual-write)
    lastAnalysis = analysis;
    saveAnalysisToStorage(analysis);
    saveCalcStateToStorage(analysis);
    
    // 12. Event dispatchen (CartSync lauscht darauf)
    if (typeof calculatedBottles === 'number' && 
        _isFinite(calculatedBottles) && 
        calculatedBottles >= 1) {
      dispatchAnalysisUpdated(analysis);
    } else {
      console.warn('Isocal: calculatedBottles ungültig, Event skipped', calculatedBottles);
    }
    
    // 13. Return
    _log('Isocal calculateFinalAnalysis:', analysis);
    return analysis;
  }

  /**
   * Getter für letztes Analyse-Objekt
   */
  function getFinalAnalysis() {
    if (lastAnalysis) return lastAnalysis;
    return loadAnalysisFromStorage();
  }

  /**
   * Setzt In-Memory-Analyse + Storage zurück.
   * Wird vom Debug-Reset und clearFlowState() genutzt.
   */
  function clearAnalysis() {
    lastAnalysis = null;
    /* Storage-Keys (alle Varianten) leeren — localStorage */
    try { localStorage.removeItem(LEGACY_ANALYSIS_KEY); } catch(_) {}
    try { localStorage.removeItem('__ISOWIZ_analysis'); } catch(_) {}
    try { localStorage.removeItem(LEGACY_CALCSTATE_KEY); } catch(_) {}
    try { localStorage.removeItem('isodash-wiz:analysis'); } catch(_) {}
    try { localStorage.removeItem('isodash-wiz:calcState'); } catch(_) {}
    /* Storage-Keys — sessionStorage (IsoDash.Storage nutzt auch session!) */
    try { sessionStorage.removeItem('isodash-wiz:analysis'); } catch(_) {}
    try { sessionStorage.removeItem('isodash-wiz:calcState'); } catch(_) {}
    /* IsoDash.Storage API (räumt beide Stores auf) */
    if (_hasStorage()) {
      try { IsoDash.Storage.remove(STORAGE_ANALYSIS_KEY); } catch(_) {}
      try { IsoDash.Storage.remove(STORAGE_CALCSTATE_KEY); } catch(_) {}
    }
  }

  // ============================================================================
  // LEGACY-WRAPPER
  // ============================================================================

  /**
   * Legacy-Wrapper für calculateResults
   * @deprecated Verwende calculateFinalAnalysis direkt
   */
  function calculateResults(state) {
    var setCalculatedBottles = state.setCalculatedBottles;
    var setCalculatedPrice = state.setCalculatedPrice;
    var setFachfirmaKosten = state.setFachfirmaKosten;
    var setEinsparung = state.setEinsparung;
    var setEinsparungPercent = state.setEinsparungPercent;
    
    var analysis = calculateFinalAnalysis(state);
    
    if (typeof setCalculatedBottles === 'function') {
      setCalculatedBottles(analysis.calculatedBottles);
    }
    if (typeof setCalculatedPrice === 'function') {
      setCalculatedPrice(analysis.totalPriceIsofin);
    }
    if (typeof setFachfirmaKosten === 'function') {
      setFachfirmaKosten(analysis.fachfirmaPrice);
    }
    if (typeof setEinsparung === 'function') {
      setEinsparung(analysis.savingsAmount);
    }
    if (typeof setEinsparungPercent === 'function') {
      setEinsparungPercent(analysis.savingsPercent);
    }
    
    return {
      bottleMenge: analysis.calculatedBottles,
      einzelpreis: analysis.unitPrice,
      gesamtpreis: analysis.totalPriceIsofin,
      firmaKosten: analysis.fachfirmaPrice,
      ersparnis: analysis.savingsAmount,
      prozent: analysis.savingsPercent
    };
  }

  /**
   * @deprecated Verwende calculateFinalAnalysis direkt
   */
  function calculateAll(state) {
    console.warn('Isocal: calculateAll ist deprecated, nutze calculateFinalAnalysis');
    return calculateFinalAnalysis(state);
  }

  function getEinzelpreis(bottleCount) {
    return getUnitPrice(bottleCount);
  }

  // ============================================================================
  // HILFSFUNKTIONEN
  // ============================================================================

  function generateVariationFaktor() {
    var config = (IsoDash.Config && IsoDash.Config.calculation) || {};
    var min = _isFinite(parseFloat(config.variationMin)) ? parseFloat(config.variationMin) : 0.8;
    var max = _isFinite(parseFloat(config.variationMax)) ? parseFloat(config.variationMax) : 1.2;
    if (max < min) {
      var tmp = max;
      max = min;
      min = tmp;
    }
    return min + Math.random() * (max - min);
  }

  function transferBottleCountToGemPages(bottleCount) {
    try {
      var inputs = document.querySelectorAll('input[name="product-quantity"]');
      inputs.forEach(function(input) {
        input.value = bottleCount;
        input.setAttribute('value', bottleCount);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } catch (error) {
      console.error('Isocal transferBottleCountToGemPages:', error);
    }
  }

  // ============================================================================
  // RISIKO-MODUL: Schadensentwicklung & Risiko-Scoring
  // ============================================================================
  // Migriert aus dashboard-mod-risiko.js — SSOT für alle Risiko-Berechnungen.
  // UI/Rendering bleibt in risiko.js, nur pure math/data hier.
  // ============================================================================

  // -- Symptom-Gewichtungen für Risiko-Scoring (Standalone-Modus) --
  var RISK_SYMPTOM_OPTS = [
    { id: 'flecken',  label: 'Feuchte Flecken / nasse Stellen',       w: { k: 0.5, h: 0.3, g: 0.2, s: 0.2 } },
    { id: 'salz',     label: 'Salzausbl\u00fchungen / Salpeter',      w: { k: 1.0, h: 0.8, g: 0.3, s: 2.0 } },
    { id: 'schimmel', label: 'Schimmelbildung',                        w: { k: 1.2, h: 1.5, g: 3.0, s: 0.5 } },
    { id: 'putz',     label: 'Putz bl\u00e4ttert ab / br\u00f6ckelt', w: { k: 1.0, h: 1.2, g: 0.3, s: 1.5 } },
    { id: 'geruch',   label: 'Muffiger / modriger Geruch',             w: { k: 0.3, h: 0.5, g: 1.5, s: 0.2 } },
    { id: 'moertel',  label: 'M\u00f6rtel br\u00f6selt aus Fugen',    w: { k: 1.5, h: 1.0, g: 0.2, s: 3.0 } }
  ];
  var RISK_SYMPTOM_MAX = { k: 5.5, h: 5.3, g: 5.5, s: 7.4 };

  function riskClamp(v) {
    return Math.max(0.05, Math.min(0.95, v));
  }

  /**
   * Normalisiert Symptom-Flags aus einem State-Objekt.
   * @param {Object} state - { symptoms: { waterStains, saltEfflorescence, ... } }
   * @returns {Object} normalisierte boolean-Flags
   */
  function normalizeDamageSymptoms(state) {
    var symptoms = state && state.symptoms ? state.symptoms : {};
    return {
      waterStains: !!symptoms.waterStains,
      saltEfflorescence: !!symptoms.saltEfflorescence,
      plasterDamage: !!symptoms.plasterDamage,
      moldVisible: !!symptoms.moldVisible,
      mustyOdor: !!symptoms.mustyOdor
    };
  }

  /**
   * Schätzt Dauer des Sperrenaufbaus in Tagen.
   * @param {Object} opts - { wallThicknessCm, masonryType, isConcrete, moistureType }
   * @returns {{ low: number, high: number }}
   */
  function estimateBarrierBuildDays(opts) {
    var wallThicknessCm = Number(opts.wallThicknessCm) || 36;
    var masonryType = String(opts.masonryType || '').toLowerCase();
    var isConcrete = !!opts.isConcrete;
    var moistureType = String(opts.moistureType || 'bottom');
    var low = 1, high = 4;
    if (isConcrete) { low += 1; high += 2; }
    if (wallThicknessCm >= 50) { high += 2; }
    else if (wallThicknessCm >= 36) { high += 1; }
    if (moistureType === 'side') { high += 1; }
    if (masonryType.indexOf('porenbeton') >= 0 || masonryType.indexOf('ytong') >= 0) {
      low = 1; high = Math.min(high, 3);
    }
    low = Math.max(1, low);
    high = Math.max(low, Math.min(high, 7));
    return { low: low, high: high };
  }

  /**
   * Berechnet Symptom-Score (0–98) aus boolean-Flags.
   */
  function calculateSymptomScore(symptoms) {
    var score = 0;
    if (symptoms.waterStains) score += 10;
    if (symptoms.saltEfflorescence) score += 18;
    if (symptoms.plasterDamage) score += 24;
    if (symptoms.mustyOdor) score += 16;
    if (symptoms.moldVisible) score += 30;
    return score;
  }

  /**
   * Berechnet Zeitfortschritts-Score basierend auf Monaten ohne Behandlung.
   */
  function calculateTimeProgressScore(monthsUntreated) {
    var m = Math.max(0, Number(monthsUntreated) || 0);
    if (m <= 1) return 5;
    if (m <= 3) return 12;
    if (m <= 6) return 22;
    if (m <= 12) return 38;
    if (m <= 18) return 52;
    if (m <= 24) return 64;
    return 72;
  }

  /**
   * Bestimmt Trocknungsträgheitsfaktor basierend auf Bauteileigenschaften.
   * @returns {number} Faktor zwischen 0.75 und 1.85
   */
  function getDryingInertiaFactor(opts) {
    var factor = 1.0;
    var wallThicknessCm = Number(opts.wallThicknessCm) || 0;
    var masonryType = String(opts.masonryType || '').toLowerCase();
    var isConcrete = !!opts.isConcrete;
    var moistureType = String(opts.moistureType || 'bottom');
    if (wallThicknessCm >= 50) factor += 0.35;
    else if (wallThicknessCm >= 36) factor += 0.18;
    else if (wallThicknessCm <= 17) factor -= 0.08;
    if (isConcrete) factor += 0.25;
    if (masonryType.indexOf('kalksand') >= 0) factor += 0.10;
    if (masonryType.indexOf('ziegel') >= 0) factor += 0.04;
    if (masonryType.indexOf('porenbeton') >= 0 || masonryType.indexOf('ytong') >= 0) factor -= 0.08;
    if (moistureType === 'side') factor += 0.10;
    return Math.max(0.75, Math.min(factor, 1.85));
  }

  /**
   * Kern-Berechnung der Schadensprogression.
   * @returns {{ moistureSpreadScore, surfaceDamageScore, healthRiskScore, overallSeverityScore, severityPhase }}
   */
  function calculateDamageProgressionCore(opts) {
    var symptoms = opts.symptoms;
    var timeScore = calculateTimeProgressScore(opts.monthsUntreated);
    var symptomScore = calculateSymptomScore(symptoms);
    var inertia = getDryingInertiaFactor(opts);
    var moistureSpreadScore = Math.round(Math.min(100, (timeScore * 1.10 + symptomScore * 0.45) * inertia));
    var surfaceDamageScore = Math.round(Math.min(100, (timeScore * 0.70 + symptomScore * 0.95) * inertia));
    var healthRiskScore = Math.round(Math.min(100, (timeScore * 0.45 + symptomScore * 1.15) * (opts.roomUse === 'wohnraum' ? 1.15 : 1.0)));
    var overall = Math.round((moistureSpreadScore * 0.40) + (surfaceDamageScore * 0.35) + (healthRiskScore * 0.25));
    var phase = 1;
    if (overall >= 75) phase = 4;
    else if (overall >= 55) phase = 3;
    else if (overall >= 30) phase = 2;
    return {
      moistureSpreadScore: moistureSpreadScore,
      surfaceDamageScore: surfaceDamageScore,
      healthRiskScore: healthRiskScore,
      overallSeverityScore: overall,
      severityPhase: phase
    };
  }

  /**
   * Schätzt Trocknungsdauer in Wochen.
   * @param {Object} opts - { severityPhase, dryingInertiaFactor }
   * @returns {{ low: number, high: number }}
   */
  function estimateDryingDurationWeeks(opts) {
    var phase = opts.severityPhase;
    var inertia = opts.dryingInertiaFactor;
    var low = 3, high = 8;
    if (phase === 2) { low = 6; high = 14; }
    else if (phase === 3) { low = 12; high = 28; }
    else if (phase === 4) { low = 20; high = 40; }
    low = Math.round(low * inertia);
    high = Math.round(high * inertia);
    low = Math.max(2, low);
    high = Math.max(low + 2, high);
    return { low: low, high: high };
  }

  /**
   * Schätzt Nacharbeitsaufwand basierend auf Phase und Symptomen.
   */
  function estimatePostRepairEffort(opts) {
    var phase = opts.severityPhase;
    var symptoms = opts.symptoms;
    var low = 0, high = 1;
    var level = 'niedrig', ventilationEffortLevel = 'niedrig', moldRiskLevel = 'gering';
    if (phase === 2) { low = 1; high = 2; level = 'mittel'; ventilationEffortLevel = 'mittel'; }
    else if (phase === 3) { low = 2; high = 5; level = 'hoch'; ventilationEffortLevel = 'hoch'; }
    else if (phase === 4) { low = 4; high = 10; level = 'sehr hoch'; ventilationEffortLevel = 'sehr hoch'; }
    if (symptoms.moldVisible || symptoms.mustyOdor) {
      moldRiskLevel = symptoms.moldVisible ? 'hoch' : 'erh\u00f6ht';
    }
    return {
      remediationDaysLow: low, remediationDaysHigh: high,
      postRepairLevel: level, ventilationEffortLevel: ventilationEffortLevel,
      moldRiskLevel: moldRiskLevel
    };
  }

  /**
   * Gibt Schweregrad-Label für eine Phase zurück.
   * @param {number} phase - 1..4
   * @returns {string}
   */
  function getSeverityLabel(phase) {
    if (phase === 1) return 'fr\u00fch';
    if (phase === 2) return 'moderat';
    if (phase === 3) return 'fortgeschritten';
    return 'kritisch';
  }

  /**
   * Erzeugt phasenabhängigen Guidance-Text.
   * @param {Object} data - { severityPhase }
   * @returns {{ guidanceShort: string, guidanceLong: string }}
   */
  function buildDamageGuidanceText(data) {
    if (data.severityPhase === 1) return {
      guidanceShort: 'Fr\u00fcher Schaden: Ursache jetzt stoppen, Nacharbeiten meist gering.',
      guidanceLong: 'Die Feuchtebelastung ist noch vergleichsweise fr\u00fch. Nach Einbau der Kapillarwassersperre bleibt der zus\u00e4tzliche Sanieraufwand meist \u00fcberschaubar.'
    };
    if (data.severityPhase === 2) return {
      guidanceShort: 'Mittlerer Schaden: R\u00fccktrocknung und Oberfl\u00e4chenarbeiten deutlich erh\u00f6ht.',
      guidanceLong: 'Die Feuchtigkeit sitzt voraussichtlich bereits tiefer im Bauteil. Nach Einbau der Sperre sind eine l\u00e4ngere R\u00fccktrocknung und partielle Putz-/Oberfl\u00e4chenarbeiten realistisch.'
    };
    if (data.severityPhase === 3) return {
      guidanceShort: 'Fortgeschrittener Schaden: l\u00e4ngere Trocknung, h\u00f6here Nacharbeit, erh\u00f6htes Raumklimarisiko.',
      guidanceLong: 'Die Wand ist voraussichtlich deutlich tiefer durchfeuchtet. Ursache stoppen bleibt sinnvoll, jedoch steigen R\u00fccktrocknungsdauer, L\u00fcftungsaufwand und Nacharbeiten deutlich an.'
    };
    return {
      guidanceShort: 'Kritischer Schaden: gesundheitliche und bauliche Folgen stark erh\u00f6ht.',
      guidanceLong: 'Die Belastung ist voraussichtlich stark fortgeschritten. Auch nach dem Stoppen der Ursache ist mit l\u00e4ngerer Trocknung, intensiver Nacharbeit und hoher Raumklima-/Schimmelrelevanz zu rechnen.'
    };
  }

  /**
   * Berechnet vollständige Schadensprogression — Orchestrator-Funktion.
   * @param {Object} opts - { monthsUntreated, symptoms, wallThicknessCm, masonryType, isConcrete, moistureType, roomUse, workTimeRangeMin }
   * @returns {Object} Komplettes Schadensprofil
   */
  function calculateDamageProgression(opts) {
    var symptoms = normalizeDamageSymptoms({ symptoms: opts.symptoms });
    var barrierBuild = estimateBarrierBuildDays(opts);
    var wtr = opts.workTimeRangeMin || { low: 120, high: 240 };
    var actionHoursLow = Math.max(1, Math.round((wtr.low || 120) / 60));
    var actionHoursHigh = Math.max(actionHoursLow, Math.ceil((wtr.high || 240) / 60));
    var dryingInertiaFactor = getDryingInertiaFactor(opts);
    var core = calculateDamageProgressionCore({
      monthsUntreated: opts.monthsUntreated,
      symptoms: symptoms,
      wallThicknessCm: opts.wallThicknessCm,
      masonryType: opts.masonryType,
      isConcrete: opts.isConcrete,
      moistureType: opts.moistureType,
      roomUse: opts.roomUse
    });
    var drying = estimateDryingDurationWeeks({
      severityPhase: core.severityPhase,
      dryingInertiaFactor: dryingInertiaFactor
    });
    var postRepair = estimatePostRepairEffort({
      severityPhase: core.severityPhase,
      symptoms: symptoms
    });
    var guidance = buildDamageGuidanceText(core);
    return {
      monthsUntreated: Math.max(0, Number(opts.monthsUntreated) || 0),
      immediateActionHoursLow: actionHoursLow,
      immediateActionHoursHigh: actionHoursHigh,
      barrierBuildDaysLow: barrierBuild.low,
      barrierBuildDaysHigh: barrierBuild.high,
      dryingWeeksLow: drying.low,
      dryingWeeksHigh: drying.high,
      remediationDaysLow: postRepair.remediationDaysLow,
      remediationDaysHigh: postRepair.remediationDaysHigh,
      moistureSpreadScore: core.moistureSpreadScore,
      surfaceDamageScore: core.surfaceDamageScore,
      healthRiskScore: core.healthRiskScore,
      overallSeverityScore: core.overallSeverityScore,
      severityPhase: core.severityPhase,
      severityLabel: getSeverityLabel(core.severityPhase),
      postRepairLevel: postRepair.postRepairLevel,
      ventilationEffortLevel: postRepair.ventilationEffortLevel,
      moldRiskLevel: postRepair.moldRiskLevel,
      guidanceShort: guidance.guidanceShort,
      guidanceLong: guidance.guidanceLong
    };
  }

  /**
   * Baut Chart-Datensätze für die Schadensentwicklungs-Visualisierung.
   * @param {Object} currentSymptoms - { waterStains, saltEfflorescence, ... }
   * @param {Object} baseOpts - Gebäudeparameter
   * @returns {{ labels: string[], spread: number[], health: number[], effort: number[], now: number[] }}
   */
  function buildRiskChartData(currentSymptoms, baseOpts) {
    var months = [0, 3, 6, 9, 12, 18, 24];
    var spread = [], health = [], effort = [], isofinNow = [];
    var allDp = months.map(function(m) {
      return calculateDamageProgression({
        monthsUntreated: m,
        symptoms: currentSymptoms,
        wallThicknessCm: baseOpts.wallThicknessCm,
        masonryType: baseOpts.masonryType,
        isConcrete: baseOpts.isConcrete,
        moistureType: baseOpts.moistureType,
        roomUse: baseOpts.roomUse,
        workTimeRangeMin: baseOpts.workTimeRangeMin
      });
    });
    var dp0 = allDp[0];
    var startSpread = dp0.moistureSpreadScore;
    var startHealth = dp0.healthRiskScore;
    var burden0 = Math.round(
      (dp0.surfaceDamageScore * 0.45) +
      (Math.min(100, Math.round((dp0.dryingWeeksHigh / 40) * 100)) * 0.35) +
      (Math.min(100, Math.round((dp0.remediationDaysHigh / 10) * 100)) * 0.20)
    );
    var startEffort = Math.min(100, burden0);
    var sharedStart = Math.round((startSpread + startHealth + startEffort) / 3);
    sharedStart = Math.max(sharedStart, 15);
    var isofinFloor = 5;
    if (dp0.severityPhase >= 2) isofinFloor = 7;
    if (dp0.severityPhase >= 3) isofinFloor = 9;
    months.forEach(function(m, idx) {
      var dp = allDp[idx];
      var burden = Math.round(
        (dp.surfaceDamageScore * 0.45) +
        (Math.min(100, Math.round((dp.dryingWeeksHigh / 40) * 100)) * 0.35) +
        (Math.min(100, Math.round((dp.remediationDaysHigh / 10) * 100)) * 0.20)
      );
      spread.push(Math.max(idx === 0 ? sharedStart : dp.moistureSpreadScore, idx === 0 ? sharedStart : 0));
      health.push(Math.max(idx === 0 ? sharedStart : dp.healthRiskScore, idx === 0 ? sharedStart : 0));
      effort.push(Math.max(idx === 0 ? sharedStart : Math.min(100, burden), idx === 0 ? sharedStart : 0));
      if (idx === 0) {
        isofinNow.push(sharedStart);
      } else if (idx === 1) {
        isofinNow.push(Math.round(sharedStart * 0.35 + isofinFloor * 0.65));
      } else {
        isofinNow.push(isofinFloor);
      }
    });
    return {
      labels: months.map(function(m) { return m === 0 ? 'Heute' : '+' + m + ' Mon.'; }),
      spread: spread, health: health, effort: effort, now: isofinNow
    };
  }

  /**
   * Formatiert Trocknungsdauer-Bereich als Text mit Einheit.
   * @returns {{ text: string, unit: string }}
   */
  function formatDryingRange(low, high) {
    if (low >= 10) {
      var mLow = Math.max(2, Math.round(low / 4.3));
      var mHigh = Math.max(mLow + 1, Math.round(high / 4.3));
      return { text: 'ca. ' + mLow + '\u2013' + mHigh, unit: 'Monate' };
    }
    return { text: 'ca. ' + low + '\u2013' + high, unit: 'Wochen' };
  }

  /**
   * Berechnet Risiko-Scores im Standalone-Modus (ohne FA-Daten).
   * @param {number} dNorm - Normalisierter Dauerwert (0..1)
   * @param {string[]} symptomIds - Array von Symptom-IDs
   * @returns {{ kosten: number, hauswert: number, gesundheit: number, substanz: number }}
   */
  function calculateRiskScoresStandalone(dNorm, symptomIds) {
    var sK = 0, sH = 0, sG = 0, sS = 0;
    var hSch = false, hGer = false, hMoe = false, hSal = false;
    symptomIds.forEach(function(sid) {
      var o = RISK_SYMPTOM_OPTS.find(function(x) { return x.id === sid; });
      if (o) { sK += o.w.k; sH += o.w.h; sG += o.w.g; sS += o.w.s; }
      if (sid === 'schimmel') hSch = true;
      if (sid === 'geruch') hGer = true;
      if (sid === 'moertel') hMoe = true;
      if (sid === 'salz') hSal = true;
    });
    sK = Math.min(1, sK / RISK_SYMPTOM_MAX.k);
    sH = Math.min(1, sH / RISK_SYMPTOM_MAX.h);
    sG = Math.min(1, sG / RISK_SYMPTOM_MAX.g);
    sS = Math.min(1, sS / RISK_SYMPTOM_MAX.s);
    return {
      kosten:     riskClamp(dNorm * 0.40 + sK * 0.35 + 0.08 + (dNorm >= 0.66 ? 0.12 : 0)),
      hauswert:   riskClamp(dNorm * 0.35 + sH * 0.30 + (hSch ? 0.15 : 0) + 0.05),
      gesundheit: riskClamp((hSch ? 0.40 : 0) + (hGer ? 0.15 : 0) + dNorm * 0.25 + sG * 0.10 + 0.03),
      substanz:   riskClamp(dNorm * 0.40 + (hMoe ? 0.30 : 0) + (hSal ? 0.15 : 0) + sS * 0.08 + 0.03)
    };
  }

  /**
   * Berechnet Risiko-Scores aus FA-Antworten (Feuchte-Analyse).
   * @param {number} dNorm - Normalisierter Dauerwert (0..1)
   * @param {Object} faAnswers - FA-Antworten-Objekt (q2, q3, q4, q5, q7)
   * @returns {{ kosten: number, hauswert: number, gesundheit: number, substanz: number } | null}
   */
  function calculateRiskScoresFA(dNorm, faAnswers) {
    if (!faAnswers) return null;
    var fa = faAnswers;
    var sym = {};
    if (fa.q2 === 'smell_only') sym.geruch = true;
    if (fa.q2 === 'plaster_damage') sym.putz = true;
    if (fa.q2 === 'wont_dry' || fa.q2 === 'dark_permanent' || fa.q2 === 'cold_clammy') sym.flecken = true;
    if (fa.q4 === 'mineral_powder' || fa.q4 === 'mineral_crystal') sym.salz = true;
    var sev = 0.3, alt = 0.5, lage = 'eg';
    if (fa.q3 === 'bottom_band') sev = 0.2;
    if (fa.q3 === 'bottom_knee') sev = 0.5;
    if (fa.q3 === 'bottom_hip') sev = 0.7;
    if (fa.q3 === 'everywhere') sev = 0.8;
    if (fa.q7 === 'pre_1930') alt = 1.0;
    if (fa.q7 === 'between_1930_1970') alt = 0.75;
    if (fa.q7 === 'between_1970_2000') alt = 0.4;
    if (fa.q7 === 'after_2000') alt = 0.15;
    if (fa.q5 === 'basement') lage = 'keller';
    if (fa.q5 === 'upper_floor') lage = 'og';
    var lf = { keller: 0.12, eg: 0.08, og: 0.02 }[lage] || 0.08;
    var sK = 0, sH = 0, sG = 0, sS = 0;
    RISK_SYMPTOM_OPTS.forEach(function(o) {
      if (sym[o.id]) { sK += o.w.k; sH += o.w.h; sG += o.w.g; sS += o.w.s; }
    });
    sK = Math.min(1, sK / RISK_SYMPTOM_MAX.k);
    sH = Math.min(1, sH / RISK_SYMPTOM_MAX.h);
    sG = Math.min(1, sG / RISK_SYMPTOM_MAX.g);
    sS = Math.min(1, sS / RISK_SYMPTOM_MAX.s);
    return {
      kosten:     riskClamp(dNorm * 0.30 + sK * 0.25 + sev * 0.25 + lf + alt * 0.08),
      hauswert:   riskClamp(dNorm * 0.25 + sH * 0.25 + sev * 0.25 + lf + (sym.schimmel ? 0.12 : 0)),
      gesundheit: riskClamp(dNorm * 0.20 + sG * 0.30 + (sym.schimmel ? 0.30 : 0) + (sym.geruch ? 0.10 : 0) + 0.03),
      substanz:   riskClamp(dNorm * 0.30 + sS * 0.25 + alt * 0.20 + sev * 0.15 + (sym.moertel ? 0.08 : 0))
    };
  }

  /**
   * Gibt Risiko-Endlabel für eine Kategorie und einen Wert zurück.
   * @param {string} key - 'kosten' | 'hauswert' | 'gesundheit' | 'substanz'
   * @param {number} v - Score (0..1)
   * @returns {string}
   */
  function getRiskEndLabel(key, v) {
    if (key === 'kosten')     return v > 0.6 ? 'Fachfirma n\u00f6tig' : v > 0.35 ? 'Aufwand steigt' : 'Noch g\u00fcnstig';
    if (key === 'hauswert')   return v > 0.5 ? 'bis \u221220% Wert'    : v > 0.3  ? 'Wert sinkt'     : 'Noch stabil';
    if (key === 'gesundheit') return v > 0.5 ? 'Atemwege gef\u00e4hrdet' : v > 0.3 ? 'Sporenbelastung' : 'Gering';
    if (key === 'substanz')   return v > 0.5 ? 'M\u00f6rtelzerfall'      : v > 0.3 ? 'Kalk l\u00f6st sich' : 'Noch intakt';
    return '';
  }

  /**
   * Berechnet Immobilien-Wertverlust-Projektion.
   * @param {number} propertyValue - Immobilienwert in EUR
   * @param {number} lossPercentMin - Min. Verlust-Prozentsatz (z.B. 0.05)
   * @param {number} lossPercentMax - Max. Verlust-Prozentsatz (z.B. 0.15)
   * @returns {{ lossMin: number, lossMax: number }}
   */
  function calculatePropertyLoss(propertyValue, lossPercentMin, lossPercentMax) {
    var pv = parseFloat(propertyValue) || 0;
    var minPct = parseFloat(lossPercentMin) || 0.05;
    var maxPct = parseFloat(lossPercentMax) || 0.15;
    return {
      lossMin: Math.round(pv * minPct),
      lossMax: Math.round(pv * maxPct)
    };
  }

  /**
   * Berechnet Investitionsanteil am Wertverlust.
   * @param {number} investmentCost - Sanierungskosten
   * @param {number} lossMin - Minimaler Wertverlust
   * @returns {string} Prozentwert als String (z.B. "2.3") oder "–"
   */
  function calculateInvestmentRatio(investmentCost, lossMin) {
    if (lossMin > 0) return ((investmentCost / lossMin) * 100).toFixed(1);
    return '\u2013';
  }

  /**
   * Berechnet Belastungsindex (Burden) aus Schadensdaten.
   * @param {Object} dp - Schadensprogression-Objekt
   * @returns {number} 0..100
   */
  function calculateBurdenIndex(dp) {
    return Math.round(
      (dp.surfaceDamageScore * 0.45) +
      (Math.min(100, Math.round((dp.dryingWeeksHigh / 40) * 100)) * 0.35) +
      (Math.min(100, Math.round((dp.remediationDaysHigh / 10) * 100)) * 0.20)
    );
  }

  // ============================================================================
  // ÖFFENTLICHE API
  // ============================================================================

  return {
    // Zentrale Funktionen
    calculateFinalAnalysis: calculateFinalAnalysis,
    getFinalAnalysis: getFinalAnalysis,
    clearAnalysis: clearAnalysis,

    // Kernberechnungen
    getWallThicknessFactor: getWallThicknessFactor,
    getUnitPrice: getUnitPrice,
    getOriginalPrice: getOriginalPrice,
    getDiscountInfo: getDiscountInfo,
    
    // Storage
    loadAnalysisFromStorage: loadAnalysisFromStorage,
    loadCalcStateFromStorage: loadCalcStateFromStorage,
    
    // Formatierung
    formatCurrency: formatCurrency,
    formatNumber: formatNumber,
    
    // Hilfsfunktionen
    generateVariationFaktor: generateVariationFaktor,
    
    // Legacy (abwärtskompatibel)
    calculateResults: calculateResults,
    calculateAll: calculateAll,
    getEinzelpreis: getEinzelpreis,
    transferBottleCountToGemPages: transferBottleCountToGemPages,
    
    // Cart-Sync Proxies (werden von 26-isodash-wiz-cart.js überschrieben wenn geladen)
    // Diese Stubs verhindern Fehler falls cart.js noch nicht geladen ist.
    syncCartWithCalculation: function(qty) {
      console.warn('Isocal: syncCartWithCalculation → 26-isodash-wiz-cart.js noch nicht geladen');
      return Promise.resolve({ synced: false, reason: 'cart_module_not_loaded' });
    },
    debouncedCartSync: function() {
      console.warn('Isocal: debouncedCartSync → 26-isodash-wiz-cart.js noch nicht geladen');
    },
    forceCartSync: function() {
      console.warn('Isocal: forceCartSync → 26-isodash-wiz-cart.js noch nicht geladen');
      return Promise.resolve({ synced: false, reason: 'cart_module_not_loaded' });
    },
    flushPendingCartSync: function() {
      console.warn('Isocal: flushPendingCartSync → 26-isodash-wiz-cart.js noch nicht geladen');
      return Promise.resolve({ synced: false, reason: 'cart_module_not_loaded' });
    },
    resetSyncGuard: function() {},
    updateCartBadgeDOM: function() {},
    refreshCartBadgeFromCartJS: function() {},
    
    // Risiko-Modul: Schadensentwicklung
    normalizeDamageSymptoms: normalizeDamageSymptoms,
    estimateBarrierBuildDays: estimateBarrierBuildDays,
    calculateSymptomScore: calculateSymptomScore,
    calculateTimeProgressScore: calculateTimeProgressScore,
    getDryingInertiaFactor: getDryingInertiaFactor,
    calculateDamageProgressionCore: calculateDamageProgressionCore,
    estimateDryingDurationWeeks: estimateDryingDurationWeeks,
    estimatePostRepairEffort: estimatePostRepairEffort,
    getSeverityLabel: getSeverityLabel,
    buildDamageGuidanceText: buildDamageGuidanceText,
    calculateDamageProgression: calculateDamageProgression,
    buildRiskChartData: buildRiskChartData,
    formatDryingRange: formatDryingRange,
    calculateBurdenIndex: calculateBurdenIndex,

    // Risiko-Modul: Scoring
    calculateRiskScoresStandalone: calculateRiskScoresStandalone,
    calculateRiskScoresFA: calculateRiskScoresFA,
    getRiskEndLabel: getRiskEndLabel,
    calculatePropertyLoss: calculatePropertyLoss,
    calculateInvestmentRatio: calculateInvestmentRatio,
    RISK_SYMPTOM_OPTS: RISK_SYMPTOM_OPTS,

    // Meta
    VERSION: VERSION
  };

})();
})(window);